import React from 'react';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles';
import DashboardTemplate from "../../Common/DashboardTemplate";
import {
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    TablePagination,
    TextField,
    Button,
    Typography,
    Tab,
    Tabs
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import axios from 'axios';
import {ServiceLocator} from "../../../Services/ServiceLocator";
import DrawerMenuRight from "../../Common/DrawerMenuRight";
import { v4 as uuid } from 'uuid';
import { fromUtcForDisplay, formatUtcForDisplay, toUtcForStorage } from '../../../utils/dateUtils';
import { findPackingSlipBaseNameConflict } from '../../../utils/packingSlipNameUtils';
import { navigateToPackingSlipBoxEditor, isPackingSlipFullyReceived, getPackingSlipItemCount } from '../../../utils/packingSlipShipmentUtils';
import { followAppLink } from '../../../utils/appLink';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEye, faChevronDown, faChevronRight} from "@fortawesome/free-solid-svg-icons";
import FirstPageIcon from '@material-ui/icons/FirstPage';
import LastPageIcon from '@material-ui/icons/LastPage';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import {FilePond, registerPlugin} from 'react-filepond';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import 'filepond/dist/filepond.min.css';
import PackingSlipDetails from './PackingSlipDetails';

import { AuthenticationService } from "../../../Services/AuthenticationService";
import { filterPersistenceService } from "../../../Services/FilterPersistenceService";
import { getNumberInputDisplayValue, parseNumberInputValue } from "../../../utils/numberInputUtils";
import PartDisplay from '../../Common/PartDisplay';
import { PurchaseLineLink } from '../../Common/PurchaseLineLink';
import PartAutocompleteOption from '../../Common/PartAutocompleteOption';
// Register FilePond plugins
registerPlugin(FilePondPluginFileValidateSize);

const DEFAULT_OUTSTANDING_PARTS_SORT_BY = 'purchaseOrderNumber';
const DEFAULT_OUTSTANDING_PARTS_SORT_DIRECTION = 'asc';

const styles = theme => ({
    wrapper: {
        margin: -5,
    },
    filterToggle: {
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '2px 10px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        minWidth: '150px',
        '& .MuiCheckbox-root': {
            padding: '0px',
            color: '#35d2d9',
            '&.Mui-checked': {
                color: '#35d2d9',
            },
        }
    },
    filterLabel: {
        marginLeft: '8px',
        fontSize: '14px',
        color: 'rgba(0, 0, 0, 0.87)',
    },
    expandedBoxRow: {
        backgroundColor: '#f8f9fa',
        '&:hover': {
            backgroundColor: '#e9ecef',
        },
    }
});

let adminUsers = [];

class AdminPackingSlips extends React.Component {
    state = {
        loading: true,
        user: null,
        permissions: [],
        adminUsers: [],
        vendors: [],
        parts: [],
        partFamilies: [],
        page: 0,
        rowsPerPage: 100,
        searchTerm: '',
        showOnlyUnreceived: false,
        packingSlipDrawerOpen: false,
        packingSlipDetailsOpen: false,
        selectedPackingSlipId: null,
        selectedVendor: '',
        outstandingParts: [],
        allPurchasedParts: [], // Store all purchased parts for part info lookup
        selectedParts: {},
        selectedPartsQuantities: {},
        creatingPackingSlip: false,
        vendorNames: [],
        error: null,
        packingSlipName: '',
        packingSlipFiles: [],
        packingSlipTrackingNumber: '',
        numberOfBoxes: 1,
        currentBoxTab: 1,
        boxesSelectedParts: {1: {}},
        boxesSelectedPartsQuantities: {1: {}},
        sortBy: DEFAULT_OUTSTANDING_PARTS_SORT_BY,
        sortDirection: DEFAULT_OUTSTANDING_PARTS_SORT_DIRECTION,
        partsSearchTerm: '',
        showSelected: false,
        // Add sorting state for main packing slips table
        packingSlipsSortBy: 'dateCreated',
        packingSlipsSortDirection: 'desc',
        // File cloning state
        originalFileUrl: '',
        originalFileName: '',
        // Multi-box shipment state
        multiBoxShipmentViewOpen: false,
        selectedMultiBoxShipment: null,
        // Receiving state for multi-box shipments
        receivingBoxId: null,
        receivingQuantities: {},
        isReceiving: false,
        // Row expansion state for multi-box shipments
        expandedRows: new Set(),
        // Edit mode state
        isEditMode: false,
        editingPackingSlipId: null,
        editingMultiBoxShipment: null,
        editingBoxes: [],
        // Loading state for outstanding parts
        loadingOutstandingParts: false,
        // Override validation state
        overrideValidation: false,
        showOverrideConfirmation: false,
        // Deleting entire shipment state
        deletingEntireShipment: false,
        // View mode inside multi-box shipment drawer
        multiBoxShipmentViewMode: 'per-box'
    };

    constructor(props) {
        super(props);
        globalProps = props;

        const jwtToken = AuthenticationService.getInstance().getToken();
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + jwtToken;
    }

    async componentDidMount() {
        this.loadSavedFilterSettings();
        await this.loadData();

        const editId = new URLSearchParams(this.props.location?.search || '').get('edit');
        if (editId) {
            const loadedSlips = (this.state.packingSlips || []).length > 0
                ? this.state.packingSlips
                : await ServiceLocator.Instance.getPartService().getAllPackingSlips();
            const slip = (loadedSlips || []).find((item) => item.id === editId);
            if (slip) this.openEditPackingSlip(slip);
            if (this.props.history) {
                this.props.history.replace('/admin/packing-slips');
            }
        }

        const duplicatePackingSlip = this.props.location?.state?.duplicatePackingSlip;
        if (duplicatePackingSlip) {
            this.handleDuplicatePackingSlipEvent({ detail: duplicatePackingSlip });
            if (this.props.history) {
                this.props.history.replace('/admin/packing-slips', {});
            }
        }
        
        // Listen for duplication events from PackingSlipDetails
        window.addEventListener('duplicatePackingSlip', this.handleDuplicatePackingSlipEvent);
        // Listen for edit events from PackingSlipDetails
        window.addEventListener('editPackingSlip', this.handleEditPackingSlipEvent);
    }

    componentWillUnmount() {
        window.removeEventListener('duplicatePackingSlip', this.handleDuplicatePackingSlipEvent);
        window.removeEventListener('editPackingSlip', this.handleEditPackingSlipEvent);
    }
    loadSavedFilterSettings = () => {
        const savedSettings = filterPersistenceService.loadFilterSettings('packingslips');
        if (savedSettings) {
            this.setState({
                page: savedSettings.page || 0,
                rowsPerPage: savedSettings.rowsPerPage || 100,
                searchTerm: savedSettings.searchTerm || '',
                showAdvancedInfo: savedSettings.showAdvancedInfo || false,
                showDeactivated: savedSettings.showDeactivated || false,
                showOldRevs: savedSettings.showOldRevs || false
            });
        }
    }

    saveFilterSettings = () => {
        const settings = {
            page: this.state.page,
            rowsPerPage: this.state.rowsPerPage,
            searchTerm: this.state.searchTerm,
            showAdvancedInfo: this.state.showAdvancedInfo,
            showDeactivated: this.state.showDeactivated,
            showOldRevs: this.state.showOldRevs
        };
        filterPersistenceService.saveFilterSettings('packingslips', settings);
    }

    loadData = () => {
        const self = this;
        const user = AuthenticationService.getInstance().getUser();
        if (!user || !user.userPermissions || !user.userPermissions.includes('Purchase')) {
            globalProps.history.push('/login');
            return;
        }

        self.setState({loading: true});
        return Promise.all([
            self.loadAdminUsers(), 
            self.loadParts(),
            self.loadPartFamilies(),
            self.loadPackingSlips(),
            self.loadVendorNames()
        ]).then(() => {
            self.setState({
                loading: false,
                user,
                adminUsers,
                permissions: user.userPermissions,
            });
            // Focus search bar after render
            setTimeout(() => {
                if (this.searchInputRef) {
                    this.searchInputRef.focus();
                }
            }, 100);
        });
    };

    loadAdminUsers = () => {
        const self = this;
        const userService = ServiceLocator.Instance.getUserService();
        return userService.getAdminUsers().then((adminUsersResult) => {
            adminUsers = adminUsersResult;
            self.setState({adminUsers: adminUsersResult});
        });
    };

    loadParts = () => {
        const self = this;
        const partService = ServiceLocator.Instance.getPartService();

        return partService.getAllPartsExpanded().then((parts) => {
            self.setState({parts});
        });
    };

    loadPartFamilies = () => {
        const self = this;
        const partService = ServiceLocator.Instance.getPartService();
        return partService.getPartFamilies().then((partFamilies) => {
            self.setState({partFamilies});
        });
    };

    loadPackingSlips = () => {
        const self = this;
        const partService = ServiceLocator.Instance.getPartService();

        return partService.getAllPackingSlips().then((packingSlips) => {
            self.setState({packingSlips});
        });
    };

    loadVendorNames = () => {
        const self = this;
        const partService = ServiceLocator.Instance.getPartService();

        return partService.getVendorNames().then((vendorNames) => {
            self.setState({vendorNames});
        });
    };

    loadOutstandingParts = (vendorName) => {
        const self = this;
        const partService = ServiceLocator.Instance.getPartService();

        // Set loading state to true
        self.setState({ loadingOutstandingParts: true });

        return partService.getOutstandingPurchasedPartsByVendor(vendorName).then((outstandingParts) => {
            // Calculate quantities on other packing slips for each part
            const partsWithOtherSlipsQuantity = outstandingParts.map(part => ({
                ...part,
                quantityOnOtherSlips: self.calculateQuantityOnOtherSlips(part.id)
            }));
            
            // Get the set of part IDs that are currently selected in the editing packing slip(s)
            const currentlySelectedPartIds = new Set();
            if (self.state.isEditMode) {
                if (self.state.editingPackingSlipId) {
                    // Single packing slip edit mode
                    const editingSlip = self.state.packingSlips.find(ps => ps.id === self.state.editingPackingSlipId);
                    if (editingSlip && editingSlip.items) {
                        editingSlip.items.forEach(item => {
                            currentlySelectedPartIds.add(item.purchasedPartId);
                        });
                    }
                } else if (self.state.editingMultiBoxShipment) {
                    // Multi-box shipment edit mode
                    self.state.editingBoxes.forEach(box => {
                        if (box.items) {
                            box.items.forEach(item => {
                                currentlySelectedPartIds.add(item.purchasedPartId);
                            });
                        }
                    });
                }
            }
            
            // Filter to show parts that are either:
            // 1. Not in any packing slips (quantityOnOtherSlips = 0)
            // 2. In other packing slips but only for some and not all of the ordered quantity
            // 3. Currently selected in the packing slip(s) being edited (regardless of available quantity)
            // 4. Parts with available quantity > 0
            const filteredParts = partsWithOtherSlipsQuantity.filter(part => {
                const remainingAvailable = part.quantityAvailable;
                
                // Always show parts that are currently selected in the editing packing slip(s)
                if (currentlySelectedPartIds.has(part.id)) {
                    return true;
                }
                
                // Show if there's any remaining quantity available (regardless of edit mode)
                return remainingAvailable > 0;
            });
            
            const sortedParts = self.sortOutstandingParts(filteredParts);
            self.setState({
                outstandingParts: sortedParts, 
                selectedParts: {}, 
                selectedPartsQuantities: {},
                loadingOutstandingParts: false
            });
        }).catch((error) => {
            // Set loading state to false on error as well
            self.setState({ loadingOutstandingParts: false });
            console.error('Error loading outstanding parts:', error);
        });
    };

    loadAllPurchasedPartsForVendor = (vendorName) => {
        const self = this;
        const partService = ServiceLocator.Instance.getPartService();

        // Set loading state to true
        self.setState({ loadingOutstandingParts: true });

        return partService.getAllPurchasedPartsByVendor(vendorName).then((allPurchasedParts) => {
            // Store all purchased parts for part info lookup (not filtered)
            console.log('Loaded all purchased parts for vendor:', vendorName, allPurchasedParts);
            self.setState({ allPurchasedParts: allPurchasedParts });
            
            // Calculate quantities on other packing slips for each part
            const partsWithOtherSlipsQuantity = allPurchasedParts.map(part => ({
                ...part,
                quantityOnOtherSlips: self.calculateQuantityOnOtherSlips(part.id)
            }));
            
            // Get the set of part IDs that are currently selected in the editing packing slip(s)
            const currentlySelectedPartIds = new Set();
            if (self.state.isEditMode) {
                if (self.state.editingPackingSlipId) {
                    // Single packing slip edit mode
                    const editingSlip = self.state.packingSlips.find(ps => ps.id === self.state.editingPackingSlipId);
                    if (editingSlip && editingSlip.items) {
                        editingSlip.items.forEach(item => {
                            currentlySelectedPartIds.add(item.purchasedPartId);
                        });
                    }
                } else if (self.state.editingMultiBoxShipment) {
                    // Multi-box shipment edit mode
                    self.state.editingBoxes.forEach(box => {
                        if (box.items) {
                            box.items.forEach(item => {
                                currentlySelectedPartIds.add(item.purchasedPartId);
                            });
                        }
                    });
                }
            }
            
            // Filter to show parts that are either:
            // 1. Not in any packing slips (quantityOnOtherSlips = 0)
            // 2. In other packing slips but only for some and not all of the ordered quantity
            // 3. Currently selected in the packing slip(s) being edited (regardless of available quantity)
            // 4. Parts with available quantity > 0
            const filteredParts = partsWithOtherSlipsQuantity.filter(part => {
                const remainingAvailable = part.quantityAvailable;
                
                // Always show parts that are currently selected in the editing packing slip(s)
                if (currentlySelectedPartIds.has(part.id)) {
                    return true;
                }
                
                // Show if there's any remaining quantity available (regardless of edit mode)
                return remainingAvailable > 0;
            });
            
            const sortedParts = self.sortOutstandingParts(filteredParts);
            self.setState({
                outstandingParts: sortedParts, 
                selectedParts: {}, 
                selectedPartsQuantities: {},
                loadingOutstandingParts: false
            });
        }).catch(error => {
            console.error('Error loading all purchased parts:', error);
            // Set loading state to false on error
            self.setState({ loadingOutstandingParts: false });
            // Fallback to outstanding parts if the method doesn't exist
            this.loadOutstandingParts(vendorName);
        });
    };

    calculateQuantityOnOtherSlips = (purchasedPartId) => {
        let totalOnOtherSlips = 0;
        
        // Go through all existing packing slips and sum quantities for this purchased part
        this.state.packingSlips.forEach(packingSlip => {
            // If we're in edit mode, exclude the current slip(s) being edited
            if (this.state.isEditMode) {
                if (this.state.editingPackingSlipId && packingSlip.id === this.state.editingPackingSlipId) {
                    return; // Skip this slip
                }
                if (this.state.editingMultiBoxShipment) {
                    // Skip if this slip is part of the multi-box shipment being edited
                    const isPartOfEditingShipment = this.state.editingBoxes.some(box => box.id === packingSlip.id);
                    if (isPartOfEditingShipment) {
                        return; // Skip this slip
                    }
                }
            }
            
            if (packingSlip.items) {
                packingSlip.items.forEach(item => {
                    if (item.purchasedPartId === purchasedPartId) {
                        totalOnOtherSlips += item.quantityExpected || 0;
                    }
                });
            }
        });
        
        return totalOnOtherSlips;
    };

    getOutstandingPartSortKey = (part) => (part.partNumber || part.partFamilyName || '').split(' rev ')[0].trim();

    compareOutstandingParts = (a, b, sortBy, sortDirection) => {
        const dir = sortDirection === 'asc' ? 1 : -1;

        const comparePartNumbers = () => this.getOutstandingPartSortKey(a).localeCompare(
            this.getOutstandingPartSortKey(b),
            undefined,
            { numeric: true, sensitivity: 'base' }
        );

        const comparePoNumbers = () => String(a.purchaseOrderNumber || '').localeCompare(
            String(b.purchaseOrderNumber || ''),
            undefined,
            { numeric: true, sensitivity: 'base' }
        );

        const compareOrderDates = () => {
            const aTime = new Date(a.orderDate).getTime() || 0;
            const bTime = new Date(b.orderDate).getTime() || 0;
            return aTime - bTime;
        };

        if (sortBy === 'purchaseOrderNumber') {
            const poCompare = comparePoNumbers();
            if (poCompare !== 0) {
                return poCompare * dir;
            }
            // Default: PO# then PN (PN tiebreaker always ascending)
            return comparePartNumbers();
        }

        let result = 0;
        if (sortBy === 'partNumber') {
            result = comparePartNumbers();
        } else if (sortBy === 'orderDate') {
            result = compareOrderDates();
        }

        return result * dir;
    };

    sortOutstandingParts = (parts) => {
        const { sortBy, sortDirection } = this.state;
        return this.sortOutstandingPartsWithParams(parts, sortBy, sortDirection);
    };

    handleSort = (column) => {
        const { sortBy, sortDirection } = this.state;
        let newDirection = 'asc';
        
        if (sortBy === column && sortDirection === 'asc') {
            newDirection = 'desc';
        }
        
        // Sort with the new parameters directly
        const sortedParts = this.sortOutstandingPartsWithParams(this.state.outstandingParts, column, newDirection);
        
        this.setState({
            sortBy: column,
            sortDirection: newDirection,
            outstandingParts: sortedParts
        });
    };

    sortOutstandingPartsWithParams = (parts, sortBy, sortDirection) => {
        return [...parts].sort((a, b) => this.compareOutstandingParts(a, b, sortBy, sortDirection));
    };

    handlePackingSlipsSort = (column) => {
        const { packingSlipsSortBy, packingSlipsSortDirection } = this.state;
        let newDirection = 'asc';
        
        if (packingSlipsSortBy === column && packingSlipsSortDirection === 'asc') {
            newDirection = 'desc';
        }
        
        this.setState({
            packingSlipsSortBy: column,
            packingSlipsSortDirection: newDirection,
            page: 0 // Reset to first page when sorting
        });
    };

    sortPackingSlips = (packingSlips) => {
        const { packingSlipsSortBy, packingSlipsSortDirection } = this.state;
        
        return [...packingSlips].sort((a, b) => {
            let aValue, bValue;
            
            switch (packingSlipsSortBy) {
                case 'name':
                    aValue = a.name || '';
                    bValue = b.name || '';
                    break;
                case 'vendor':
                    aValue = a.vendorName || '';
                    bValue = b.vendorName || '';
                    break;
                case 'dateCreated':
                    aValue = new Date(a.dateCreated);
                    bValue = new Date(b.dateCreated);
                    break;
                case 'trackingNumber':
                    aValue = a.trackingNumber || '';
                    bValue = b.trackingNumber || '';
                    break;
                case 'dateReceived':
                    aValue = a.dateReceived ? new Date(a.dateReceived) : new Date(0);
                    bValue = b.dateReceived ? new Date(b.dateReceived) : new Date(0);
                    break;
                case 'purchaseOrderNumber':
                    // Get the first PO number from items, or use a default value
                    const aPoNumber = a.items && a.items.length > 0 ? 
                        this.getFirstPurchaseOrderNumber(a) : '';
                    const bPoNumber = b.items && b.items.length > 0 ? 
                        this.getFirstPurchaseOrderNumber(b) : '';
                    aValue = aPoNumber;
                    bValue = bPoNumber;
                    break;
                default:
                    aValue = a.name || '';
                    bValue = b.name || '';
            }
            
            if (packingSlipsSortDirection === 'asc') {
                return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
            } else {
                return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
            }
        });
    };

    handleChangePage = (event, newPage) => {
        this.setState({page: newPage}, () => {
            this.saveFilterSettings();
        });
    };

    handleChangeRowsPerPage = (event) => {
        this.setState({
            rowsPerPage: parseInt(event.target.value, 10),
            page: 0
        }, () => {
            this.saveFilterSettings();
        });
    };

    searchPackingSlips = (event) => {
        const searchInput = event.target.value;
        this.setState({searchTerm: event.target.value, page: 0}, () => {
            this.saveFilterSettings();
        });
    };

    getFilteredPackingSlips = () => {
        let filtered = this.state.packingSlips || [];
        
        // Apply search filter
        if (this.state.searchTerm) {
            const searchTerms = this.state.searchTerm.split(' ').filter(term => term.trim() !== '');
            
            if (searchTerms.length > 0) {
                filtered = filtered.filter(ps => {
                    // All search terms must match at least one field
                    return searchTerms.every(term => {
                        const searchTerm = term.toLowerCase();
                        
                        // Check basic packing slip fields
                        const basicMatch = (ps.name && ps.name.toLowerCase().includes(searchTerm)) ||
                                          (ps.vendorName && ps.vendorName.toLowerCase().includes(searchTerm)) ||
                                          (ps.trackingNumber && ps.trackingNumber.toLowerCase().includes(searchTerm));
                        
                        if (basicMatch) return true;
                        
                        // Check parts within the packing slip
                        const partsMatch = ps.items && ps.items.some(item => {
                            const part = this.state.parts.find(p => p.part?.id === item.purchasedPart?.partId);
                            const partId = part?.part?.partId;
                            return partId && partId.toLowerCase().includes(searchTerm);
                        });
                        
                        return partsMatch;
                    });
                });
            }
        }
        
        // Apply unreceived filter
        if (this.state.showOnlyUnreceived) {
            // Show packing slips that are NOT fully received (includes unreceived and partially received)
            filtered = filtered.filter(ps => !isPackingSlipFullyReceived(ps));
        }
        
        // Apply sorting
        filtered = this.sortPackingSlips(filtered);
        
        return filtered;
    };

    filterPackingSlips = (packingSlips) => {
        let filtered = packingSlips;

        // Apply search filter
        if (this.state.searchTerm) {
            const searchTerm = this.state.searchTerm.toLowerCase();
            filtered = filtered.filter(ps => {
                // Search in basic packing slip fields
                const basicMatch = ps.name?.toLowerCase().includes(searchTerm) ||
                    ps.vendorName?.toLowerCase().includes(searchTerm) ||
                    ps.trackingNumber?.toLowerCase().includes(searchTerm);
                
                // Search in part IDs within packing slip items
                const partIdMatch = ps.items?.some(item => {
                    // Find the part using the same logic as in the render method
                    const part = this.state.parts.find(p => p.part?.id === item.purchasedPart?.partId);
                    const partId = part?.part?.partId?.toLowerCase();
                    return partId && partId.includes(searchTerm);
                });

                return basicMatch || partIdMatch;
            });
        }

        // Apply unreceived filter
        if (this.state.showOnlyUnreceived) {
            // Show packing slips that are NOT fully received (includes unreceived and partially received)
            filtered = filtered.filter(ps => !isPackingSlipFullyReceived(ps));
        }

        return filtered;
    };

    openCreatePackingSlip = () => {
        this.setState({
            packingSlipDrawerOpen: true,
            selectedVendor: '',
            outstandingParts: [],
            selectedParts: {},
            selectedPartsQuantities: {},
            creatingPackingSlip: false,
            packingSlipName: '',
            packingSlipFiles: [],
            packingSlipTrackingNumber: '',
            numberOfBoxes: 1,
            currentBoxTab: 1,
            boxesSelectedParts: {1: {}},
            boxesSelectedPartsQuantities: {1: {}},
            sortBy: DEFAULT_OUTSTANDING_PARTS_SORT_BY,
            sortDirection: DEFAULT_OUTSTANDING_PARTS_SORT_DIRECTION,
        });
    };

    handleDuplicatePackingSlipEvent = (event) => {
        const { packingSlip } = event.detail;
        
        // Pre-fill the create form with duplication data
        this.setState({
            packingSlipDrawerOpen: true,
            selectedVendor: packingSlip.vendorName,
            outstandingParts: [],
            selectedParts: {},
            selectedPartsQuantities: {},
            creatingPackingSlip: false,
            packingSlipName: packingSlip.name,
            packingSlipFiles: [],
            packingSlipTrackingNumber: packingSlip.trackingNumber || '',
            sortBy: DEFAULT_OUTSTANDING_PARTS_SORT_BY,
            sortDirection: DEFAULT_OUTSTANDING_PARTS_SORT_DIRECTION,
            // Store the original file info for cloning
            originalFileUrl: packingSlip.fileUrl || '',
            originalFileName: packingSlip.fileName || '',
        }, () => {
            // Load outstanding parts for the vendor
            if (packingSlip.vendorName) {
                this.loadOutstandingParts(packingSlip.vendorName);
            }
            
            // Pre-select the items from the original packing slip
            setTimeout(() => {
                const selectedParts = {};
                const selectedPartsQuantities = {};
                
                packingSlip.items.forEach(item => {
                    // Only pre-select if the part is available in outstandingParts
                    const availablePart = this.state.outstandingParts.find(part => part.id === item.purchasedPartId);
                    if (availablePart) {
                        selectedParts[item.purchasedPartId] = true;
                        selectedPartsQuantities[item.purchasedPartId] = Math.min(item.quantityExpected, availablePart.quantityAvailable);
                    }
                });
                
                this.setState({
                    selectedParts,
                    selectedPartsQuantities
                });
            }, 500); // Give time for outstanding parts to load
        });
    };

    handleEditPackingSlipEvent = (event) => {
        const { packingSlip } = event.detail;
        
        // Use the existing edit functionality to open the edit drawer
        this.openEditPackingSlip(packingSlip);
    };

    handleClosePackingSlipDrawer = () => {
        this.setState({
            packingSlipDrawerOpen: false,
            selectedVendor: '',
            outstandingParts: [],
            selectedParts: {},
            selectedPartsQuantities: {},
            packingSlipName: '',
            packingSlipFiles: [],
            packingSlipTrackingNumber: '',
            numberOfBoxes: 1,
            currentBoxTab: 1,
            boxesSelectedParts: {1: {}},
            boxesSelectedPartsQuantities: {1: {}},
            sortBy: DEFAULT_OUTSTANDING_PARTS_SORT_BY,
            sortDirection: DEFAULT_OUTSTANDING_PARTS_SORT_DIRECTION,
            partsSearchTerm: '',
            showSelected: false,
            // Reset file cloning state
            originalFileUrl: '',
            originalFileName: '',
            // Reset edit mode state
            isEditMode: false,
            editingPackingSlipId: null,
            editingMultiBoxShipment: null,
            editingBoxes: [],
            // Reset override validation state
            overrideValidation: false,
            showOverrideConfirmation: false
        });
    };

    // Multi-box functionality methods
    handleNumberOfBoxesChange = (numberOfBoxes) => {
        const newBoxesSelectedParts = {};
        const newBoxesSelectedPartsQuantities = {};
        
        // Initialize new boxes with empty selection
        for (let i = 1; i <= numberOfBoxes; i++) {
            newBoxesSelectedParts[i] = this.state.boxesSelectedParts[i] || {};
            newBoxesSelectedPartsQuantities[i] = this.state.boxesSelectedPartsQuantities[i] || {};
        }
        
        this.setState({
            numberOfBoxes,
            boxesSelectedParts: newBoxesSelectedParts,
            boxesSelectedPartsQuantities: newBoxesSelectedPartsQuantities,
            currentBoxTab: Math.min(this.state.currentBoxTab, numberOfBoxes)
        });
    };

    handleBoxTabChange = (boxNumber) => {
        this.setState({currentBoxTab: boxNumber});
    };

    handleBoxPartSelect = (partId, isSelected) => {
        const currentBox = this.state.currentBoxTab;
        const boxesSelectedParts = {...this.state.boxesSelectedParts};
        const boxesSelectedPartsQuantities = {...this.state.boxesSelectedPartsQuantities};
        
        if (isSelected) {
            boxesSelectedParts[currentBox][partId] = true;
            const part = this.state.outstandingParts.find(p => p.id === partId);
            if (part) {
                boxesSelectedPartsQuantities[currentBox][partId] = this.getAdjustedAvailableQuantity(partId);
            }
        } else {
            delete boxesSelectedParts[currentBox][partId];
            delete boxesSelectedPartsQuantities[currentBox][partId];
        }
        
        this.setState({
            boxesSelectedParts,
            boxesSelectedPartsQuantities
        }, () => {
            // Autofocus the quantity input field for the newly selected part
            if (isSelected) {
                setTimeout(() => {
                    const quantityInput = document.querySelector(`input[data-part-id-packing="${partId}"]`);
                    if (quantityInput) {
                        quantityInput.focus();
                        quantityInput.select();
                    }
                }, 100);
            }
        });
    };

    handleBoxPartQuantityChange = (partId, quantity) => {
        const currentBox = this.state.currentBoxTab;
        const boxesSelectedPartsQuantities = {...this.state.boxesSelectedPartsQuantities};
        
        // Store the raw string value, only parse when we need the number
        boxesSelectedPartsQuantities[currentBox][partId] = quantity;
        
        this.setState({boxesSelectedPartsQuantities});
    };

    handleQuantityInputKeyDown = (event, currentPartId) => {
        if (event.key === 'Tab' && !event.shiftKey) {
            event.preventDefault();
            
            // Get all visible quantity inputs for the current box
            const visibleParts = this.filterOutstandingParts(this.state.outstandingParts);
            const selectedParts = this.state.boxesSelectedParts[this.state.currentBoxTab] || {};
            
            // Find the current part index
            const currentIndex = visibleParts.findIndex(part => 
                part.id === currentPartId && selectedParts[part.id]
            );
            
            if (currentIndex !== -1) {
                // Find the next selected part
                let nextIndex = currentIndex + 1;
                while (nextIndex < visibleParts.length) {
                    const nextPart = visibleParts[nextIndex];
                    if (selectedParts[nextPart.id]) {
                        // Focus the next quantity input
                        setTimeout(() => {
                            const nextInput = document.querySelector(`input[data-part-id-packing="${nextPart.id}"]`);
                            if (nextInput) {
                                nextInput.focus();
                                nextInput.select();
                            }
                        }, 10);
                        return;
                    }
                    nextIndex++;
                }
                
                // If no next selected part found, focus the first selected part
                const firstSelectedPart = visibleParts.find(part => selectedParts[part.id]);
                if (firstSelectedPart) {
                    setTimeout(() => {
                        const firstInput = document.querySelector(`input[data-part-id-packing="${firstSelectedPart.id}"]`);
                        if (firstInput) {
                            firstInput.focus();
                            firstInput.select();
                        }
                    }, 10);
                }
            }
        }
    };

    cloneToBox = (targetBoxNumber) => {
        const sourceBox = this.state.currentBoxTab;
        const boxesSelectedParts = {...this.state.boxesSelectedParts};
        const boxesSelectedPartsQuantities = {...this.state.boxesSelectedPartsQuantities};
        
        const sourceParts = boxesSelectedParts[sourceBox] || {};
        const sourceQuantities = boxesSelectedPartsQuantities[sourceBox] || {};
        
        let reducedQuantityWarnings = [];
        let skippedParts = [];
        
        // Calculate quantities already allocated to other boxes (excluding target box)
        const allocatedQuantities = {};
        Object.keys(boxesSelectedParts).forEach(boxNum => {
            if (parseInt(boxNum) !== targetBoxNumber) {
                const boxParts = boxesSelectedParts[boxNum] || {};
                const boxQuantities = boxesSelectedPartsQuantities[boxNum] || {};
                
                Object.keys(boxParts).forEach(partId => {
                    if (boxParts[partId]) {
                        allocatedQuantities[partId] = (allocatedQuantities[partId] || 0) + (parseNumberInputValue(boxQuantities[partId]) || 0);
                    }
                });
            }
        });
        
        // Clone parts with quantity validation
        const newTargetParts = {};
        const newTargetQuantities = {};
        
        Object.keys(sourceParts).forEach(partId => {
            if (sourceParts[partId]) {
                const sourceQuantity = parseNumberInputValue(sourceQuantities[partId]) || 0;
                const part = this.state.outstandingParts.find(p => p.id === partId);
                
                if (part) {
                    const allocated = allocatedQuantities[partId] || 0;
                    const remainingAvailable = Math.max(0, part.quantityAvailable - allocated);
                    
                    if (remainingAvailable > 0) {
                        const clonedQuantity = Math.min(sourceQuantity, remainingAvailable);
                        newTargetParts[partId] = true;
                        newTargetQuantities[partId] = clonedQuantity.toString();
                        
                        if (clonedQuantity < sourceQuantity) {
                            reducedQuantityWarnings.push({
                                partNumber: part.partNumber || part.partFamilyName || 'Unknown',
                                originalQuantity: sourceQuantity,
                                clonedQuantity: clonedQuantity,
                                available: remainingAvailable
                            });
                        }
                    } else {
                        skippedParts.push({
                            partNumber: part.partNumber || part.partFamilyName || 'Unknown',
                            requestedQuantity: sourceQuantity,
                            available: 0
                        });
                    }
                }
            }
        });
        
        // Update state
        boxesSelectedParts[targetBoxNumber] = newTargetParts;
        boxesSelectedPartsQuantities[targetBoxNumber] = newTargetQuantities;
        
        this.setState({
            boxesSelectedParts,
            boxesSelectedPartsQuantities
        });
        
        // Show warnings
        if (reducedQuantityWarnings.length > 0) {
            const warningMessage = reducedQuantityWarnings.map(w => 
                `${w.partNumber}: Reduced from ${w.originalQuantity} to ${w.clonedQuantity} (${w.available} available)`
            ).join('\n');
            window.$alertWarning(`Some quantities were reduced due to limited availability:\n${warningMessage}`);
        }
        
        if (skippedParts.length > 0) {
            const skipMessage = skippedParts.map(s => 
                `${s.partNumber}: ${s.requestedQuantity} requested, 0 available`
            ).join('\n');
            window.$alertWarning(`${skippedParts.length} parts were not cloned due to no remaining quantity available:\n${skipMessage}`);
        }
    };

    cloneFromBox = (sourceBoxNumber) => {
        const targetBox = this.state.currentBoxTab;
        const boxesSelectedParts = {...this.state.boxesSelectedParts};
        const boxesSelectedPartsQuantities = {...this.state.boxesSelectedPartsQuantities};
        
        const sourceParts = boxesSelectedParts[sourceBoxNumber] || {};
        const sourceQuantities = boxesSelectedPartsQuantities[sourceBoxNumber] || {};
        
        let reducedQuantityWarnings = [];
        let skippedParts = [];
        
        // Calculate quantities already allocated to other boxes (excluding target box)
        const allocatedQuantities = {};
        Object.keys(boxesSelectedParts).forEach(boxNum => {
            if (parseInt(boxNum) !== targetBox) {
                const boxParts = boxesSelectedParts[boxNum] || {};
                const boxQuantities = boxesSelectedPartsQuantities[boxNum] || {};
                
                Object.keys(boxParts).forEach(partId => {
                    if (boxParts[partId]) {
                        allocatedQuantities[partId] = (allocatedQuantities[partId] || 0) + (parseNumberInputValue(boxQuantities[partId]) || 0);
                    }
                });
            }
        });
        
        // Clone parts with quantity validation
        const newTargetParts = {};
        const newTargetQuantities = {};
        
        Object.keys(sourceParts).forEach(partId => {
            if (sourceParts[partId]) {
                const sourceQuantity = parseNumberInputValue(sourceQuantities[partId]) || 0;
                const part = this.state.outstandingParts.find(p => p.id === partId);
                
                if (part) {
                    const allocated = allocatedQuantities[partId] || 0;
                    const remainingAvailable = Math.max(0, part.quantityAvailable - allocated);
                    
                    if (remainingAvailable > 0) {
                        const clonedQuantity = Math.min(sourceQuantity, remainingAvailable);
                        newTargetParts[partId] = true;
                        newTargetQuantities[partId] = clonedQuantity.toString();
                        
                        if (clonedQuantity < sourceQuantity) {
                            reducedQuantityWarnings.push({
                                partNumber: part.partNumber || part.partFamilyName || 'Unknown',
                                originalQuantity: sourceQuantity,
                                clonedQuantity: clonedQuantity,
                                available: remainingAvailable
                            });
                        }
                    } else {
                        skippedParts.push({
                            partNumber: part.partNumber || part.partFamilyName || 'Unknown',
                            requestedQuantity: sourceQuantity,
                            available: 0
                        });
                    }
                }
            }
        });
        
        // Update state
        boxesSelectedParts[targetBox] = newTargetParts;
        boxesSelectedPartsQuantities[targetBox] = newTargetQuantities;
        
        this.setState({
            boxesSelectedParts,
            boxesSelectedPartsQuantities
        });
        
        // Show warnings
        if (reducedQuantityWarnings.length > 0) {
            const warningMessage = reducedQuantityWarnings.map(w => 
                `${w.partNumber}: Reduced from ${w.originalQuantity} to ${w.clonedQuantity} (${w.available} available)`
            ).join('\n');
            window.$alertWarning(`Some quantities were reduced due to limited availability:\n${warningMessage}`);
        }
        
        if (skippedParts.length > 0) {
            const skipMessage = skippedParts.map(s => 
                `${s.partNumber}: ${s.requestedQuantity} requested, 0 available`
            ).join('\n');
            window.$alertWarning(`${skippedParts.length} parts were not cloned due to no remaining quantity available:\n${skipMessage}`);
        }
    };

    // Get all selected parts across all boxes for display in the running list
    getAllSelectedParts = () => {
        const allSelectedParts = {};
        const allSelectedQuantities = {};
        
        Object.keys(this.state.boxesSelectedParts).forEach(boxNumber => {
            const boxParts = this.state.boxesSelectedParts[boxNumber];
            const boxQuantities = this.state.boxesSelectedPartsQuantities[boxNumber];
            
            Object.keys(boxParts).forEach(partId => {
                if (boxParts[partId]) {
                    if (!allSelectedParts[partId]) {
                        allSelectedParts[partId] = {};
                        allSelectedQuantities[partId] = 0;
                    }
                    allSelectedParts[partId][boxNumber] = true;
                    allSelectedQuantities[partId] += parseNumberInputValue(boxQuantities[partId]) || 0;
                }
            });
        });
        
        return {allSelectedParts, allSelectedQuantities};
    };

    // Get original available quantity for a part (for validation purposes)
    getOriginalAvailableQuantity = (partId) => {
        const part = this.state.outstandingParts.find(p => p.id === partId);
        if (!part) return 0;
        
        let availableQuantity = part.quantityAvailable;
        
        // If we're in edit mode, we need to add back the quantities from the current slip being edited
        // because those quantities will be replaced by the new quantities
        if (this.state.isEditMode) {
            let currentSlipQuantities = 0;
            
            if (this.state.editingPackingSlipId) {
                // Editing a single packing slip
                const editingSlip = this.state.packingSlips.find(ps => ps.id === this.state.editingPackingSlipId);
                if (editingSlip) {
                    const currentItem = editingSlip.items.find(item => item.purchasedPartId === partId);
                    if (currentItem) {
                        currentSlipQuantities = currentItem.quantityExpected;
                    }
                }
            } else if (this.state.editingMultiBoxShipment) {
                // Editing a multi-box shipment
                this.state.editingBoxes.forEach(box => {
                    const currentItem = box.items.find(item => item.purchasedPartId === partId);
                    if (currentItem) {
                        currentSlipQuantities += currentItem.quantityExpected;
                    }
                });
            }
            
            // Add back the quantities from the current slip(s) being edited
            availableQuantity += currentSlipQuantities;
        }
        
        return availableQuantity;
    };

    // Check for quantity overages across all boxes
    getQuantityOverages = () => {
        const {allSelectedQuantities} = this.getAllSelectedParts();
        const overages = {};
        
        Object.keys(allSelectedQuantities).forEach(partId => {
            const availableQuantity = this.getOriginalAvailableQuantity(partId);
            
            if (allSelectedQuantities[partId] > availableQuantity) {
                overages[partId] = {
                    totalSelected: allSelectedQuantities[partId],
                    available: availableQuantity,
                    overage: allSelectedQuantities[partId] - availableQuantity
                };
            }
        });
        
        return overages;
    };

    // Get adjusted "on other slips" quantity for a part (includes quantities from other boxes on current slip)
    getAdjustedOnOtherSlipsQuantity = (partId) => {
        const part = this.state.outstandingParts.find(p => p.id === partId);
        if (!part) return 0;
        
        let onOtherSlipsQuantity = part.quantityOnOtherSlips || 0;
        
        // Add quantities from other boxes on the current slip being created/edited
        if (this.state.numberOfBoxes > 0) {
            for (let boxNumber = 1; boxNumber <= this.state.numberOfBoxes; boxNumber++) {
                // Skip the current box tab to avoid double counting
                if (boxNumber !== this.state.currentBoxTab) {
                    const boxPartQuantityStr = this.state.boxesSelectedPartsQuantities[boxNumber]?.[partId] || '0';
                    const boxPartQuantity = parseInt(boxPartQuantityStr) || 0;
                    onOtherSlipsQuantity += boxPartQuantity;
                }
            }
        }
        
        return onOtherSlipsQuantity;
    };

    // Get adjusted available quantity for a part (accounts for edit mode and other boxes on current slip)
    getAdjustedAvailableQuantity = (partId) => {
        const part = this.state.outstandingParts.find(p => p.id === partId);
        if (!part) return 0;
        
        let availableQuantity = part.quantityAvailable;
        
        // If we're in edit mode, we need to add back the quantities from the current slip being edited
        // because those quantities will be replaced by the new quantities
        if (this.state.isEditMode) {
            let currentSlipQuantities = 0;
            
            if (this.state.editingPackingSlipId) {
                // Editing a single packing slip
                const editingSlip = this.state.packingSlips.find(ps => ps.id === this.state.editingPackingSlipId);
                if (editingSlip) {
                    const currentItem = editingSlip.items.find(item => item.purchasedPartId === partId);
                    if (currentItem) {
                        currentSlipQuantities = currentItem.quantityExpected;
                    }
                }
            } else if (this.state.editingMultiBoxShipment) {
                // Editing a multi-box shipment
                this.state.editingBoxes.forEach(box => {
                    const currentItem = box.items.find(item => item.purchasedPartId === partId);
                    if (currentItem) {
                        currentSlipQuantities += currentItem.quantityExpected;
                    }
                });
            }
            
            // Add back the quantities from the current slip(s) being edited
            availableQuantity += currentSlipQuantities;
        }
        
        // Subtract quantities from other boxes on the current slip being created/edited
        if (this.state.numberOfBoxes > 0) {
            for (let boxNumber = 1; boxNumber <= this.state.numberOfBoxes; boxNumber++) {
                // Skip the current box tab to avoid double counting
                if (boxNumber !== this.state.currentBoxTab) {
                    const boxPartQuantityStr = this.state.boxesSelectedPartsQuantities[boxNumber]?.[partId] || '0';
                    const boxPartQuantity = parseInt(boxPartQuantityStr) || 0;
                    availableQuantity -= boxPartQuantity;
                }
            }
        }
        
        return Math.max(0, availableQuantity); // Ensure we don't return negative values
    };

    // Get available quantity for validation purposes (doesn't subtract other boxes)
    getAvailableQuantityForValidation = (partId) => {
        const part = this.state.outstandingParts.find(p => p.id === partId);
        if (!part) return 0;
        
        let availableQuantity = part.quantityAvailable;
        
        // If we're in edit mode, we need to add back the quantities from the current slip being edited
        // because those quantities will be replaced by the new quantities
        if (this.state.isEditMode) {
            let currentSlipQuantities = 0;
            
            if (this.state.editingPackingSlipId) {
                // Editing a single packing slip
                const editingSlip = this.state.packingSlips.find(ps => ps.id === this.state.editingPackingSlipId);
                if (editingSlip) {
                    const currentItem = editingSlip.items.find(item => item.purchasedPartId === partId);
                    if (currentItem) {
                        currentSlipQuantities = currentItem.quantityExpected;
                    }
                }
            } else if (this.state.editingMultiBoxShipment) {
                // Editing a multi-box shipment
                this.state.editingBoxes.forEach(box => {
                    const currentItem = box.items.find(item => item.purchasedPartId === partId);
                    if (currentItem) {
                        currentSlipQuantities += currentItem.quantityExpected;
                    }
                });
            }
            
            // Add back the quantities from the current slip(s) being edited
            availableQuantity += currentSlipQuantities;
        }
        
        return Math.max(0, availableQuantity); // Ensure we don't return negative values
    };

    // Check if a specific part in the current box would cause an overage
    isPartOveraged = (partId) => {
        const overages = this.getQuantityOverages();
        return !!overages[partId];
    };

    // Get validation errors for the current state
    getValidationErrors = () => {
        const self = this;
        const overages = this.getQuantityOverages();
        const errors = [];
        
        Object.keys(overages).forEach(partId => {
            const part = this.state.outstandingParts.find(p => p.id === partId);
            const overage = overages[partId];
            errors.push({
                partId,
                partNumber: part?.partNumber || part?.partFamilyName || 'Unknown',
                revision: part?.partId ? (self.state.parts?.find(x => x.part?.id === part.partId)?.part?.revision || '-') : '-',
                message: `Total quantity selected (${overage.totalSelected}) exceeds available quantity (${overage.available}) by ${overage.overage}`
            });
        });
        
        return errors;
    };

    // Multi-box shipment detection and grouping
    detectMultiBoxShipments = (packingSlips) => {
        const shipmentGroups = {};
        const multiBoxShipments = [];
        const standaloneSlips = [];
        
        packingSlips.forEach(slip => {
            const baseNameMatch = slip.name.match(/^(.+)\s-\sBox\s\d+$/);
            if (baseNameMatch) {
                const baseName = baseNameMatch[1];
                if (!shipmentGroups[baseName]) {
                    shipmentGroups[baseName] = [];
                }
                shipmentGroups[baseName].push(slip);
            } else {
                standaloneSlips.push(slip);
            }
        });
        
        // Convert groups with multiple boxes to multi-box shipments
        Object.keys(shipmentGroups).forEach(baseName => {
            const boxes = shipmentGroups[baseName];
            if (boxes.length > 1) {
                // Sort boxes by box number
                boxes.sort((a, b) => {
                    const aBoxNum = parseInt(a.name.match(/Box\s(\d+)$/)?.[1] || 0);
                    const bBoxNum = parseInt(b.name.match(/Box\s(\d+)$/)?.[1] || 0);
                    return aBoxNum - bBoxNum;
                });
                
                const boxesWithStatus = boxes.map(box => ({
                    ...box,
                    isFullyReceived: isPackingSlipFullyReceived(box)
                }));
                const totalItems = boxesWithStatus.reduce((sum, box) => sum + getPackingSlipItemCount(box), 0);
                
                multiBoxShipments.push({
                    id: `shipment-${baseName}`,
                    baseName,
                    boxes: boxesWithStatus,
                    type: 'multi-box-shipment',
                    // Aggregate data for display
                    vendorName: boxes[0].vendorName,
                    dateCreated: boxes[0].dateCreated,
                    trackingNumber: boxes[0].trackingNumber,
                    totalItems,
                    isFullyReceived: totalItems === 0 || boxesWithStatus.every(box => box.isFullyReceived),
                    hasAnyReceiving: boxesWithStatus.some(box => box.hasAnyReceiving),
                    dateReceived: boxesWithStatus.every(box => box.dateReceived) ? 
                        Math.max(...boxesWithStatus.map(box => new Date(box.dateReceived))) : null
                });
            } else {
                // Single box with naming convention, treat as standalone
                standaloneSlips.push(boxes[0]);
            }
        });
        
        return { multiBoxShipments, standaloneSlips };
    };

    openMultiBoxShipmentView = (shipment, event) => {
        const firstBox = shipment?.boxes?.[0];
        if (!firstBox) return;
        followAppLink(this.props.history, `/admin/packing-slips/box/${firstBox.id}`, event);
    };

    closeMultiBoxShipmentView = () => {
        this.setState({
            multiBoxShipmentViewOpen: false,
            selectedMultiBoxShipment: null,
            receivingBoxId: null,
            receivingQuantities: {},
            isReceiving: false,
            multiBoxShipmentViewMode: 'per-box'
        });
    };

    handleMultiBoxShipmentViewModeChange = (event, newMode) => {
        if (!newMode) return;
        this.setState({
            multiBoxShipmentViewMode: newMode,
            receivingBoxId: newMode === 'per-box' ? this.state.receivingBoxId : null,
            receivingQuantities: newMode === 'per-box' ? this.state.receivingQuantities : {}
        });
    };

    // Row expansion methods for multi-box shipments
    toggleRowExpansion = (shipmentId) => {
        this.setState(prevState => {
            const newExpandedRows = new Set(prevState.expandedRows);
            if (newExpandedRows.has(shipmentId)) {
                newExpandedRows.delete(shipmentId);
            } else {
                newExpandedRows.add(shipmentId);
            }
            return { expandedRows: newExpandedRows };
        });
    };

    isRowExpanded = (shipmentId) => {
        return this.state.expandedRows.has(shipmentId);
    };

    openBoxDetails = (box, event) => {
        followAppLink(this.props.history, `/admin/packing-slips/${box.id}`, event);
    };

    openPackingSlipBoxPage = (packingSlip, event) => {
        navigateToPackingSlipBoxEditor(globalProps.history, packingSlip.id, event);
    };

    // Edit functionality methods
    openEditPackingSlip = (packingSlip) => {
        // Check if this is part of a multi-box shipment
        const baseNameMatch = packingSlip.name.match(/^(.+)\s-\sBox\s\d+$/);
        if (baseNameMatch) {
            // This is part of a multi-box shipment, find all related boxes
            const baseName = baseNameMatch[1];
            const relatedBoxes = this.state.packingSlips.filter(ps => 
                ps.name.startsWith(baseName + ' - Box ')
            ).sort((a, b) => {
                const aBoxNum = parseInt(a.name.match(/Box\s(\d+)$/)?.[1] || 0);
                const bBoxNum = parseInt(b.name.match(/Box\s(\d+)$/)?.[1] || 0);
                return aBoxNum - bBoxNum;
            });
            
            // Find the box number of the clicked box to set the correct tab
            const clickedBoxNumber = parseInt(packingSlip.name.match(/Box\s(\d+)$/)?.[1] || 1);
            
            this.openEditMultiBoxShipment(relatedBoxes, baseName, clickedBoxNumber);
        } else {
            // This is a standalone packing slip
            this.openEditSinglePackingSlip(packingSlip);
        }
    };

    openEditSinglePackingSlip = (packingSlip) => {
        // Prepare the data for editing a single packing slip
        const selectedParts = {};
        const selectedPartsQuantities = {};
        
        packingSlip.items.forEach(item => {
            selectedParts[item.purchasedPartId] = true;
            selectedPartsQuantities[item.purchasedPartId] = item.quantityExpected;
        });

        this.setState({
            packingSlipDrawerOpen: true,
            isEditMode: true,
            editingPackingSlipId: packingSlip.id,
            editingBoxes: [{ id: packingSlip.id, name: packingSlip.name }],
            selectedVendor: packingSlip.vendorName,
            outstandingParts: [],
            selectedParts: selectedParts,
            selectedPartsQuantities: selectedPartsQuantities,
            creatingPackingSlip: false,
            packingSlipName: packingSlip.name,
            packingSlipFiles: [],
            packingSlipTrackingNumber: packingSlip.trackingNumber || '',
            numberOfBoxes: 1,
            currentBoxTab: 1,
            boxesSelectedParts: {1: selectedParts},
            boxesSelectedPartsQuantities: {1: selectedPartsQuantities},
            sortBy: DEFAULT_OUTSTANDING_PARTS_SORT_BY,
            sortDirection: DEFAULT_OUTSTANDING_PARTS_SORT_DIRECTION,
            originalFileUrl: packingSlip.fileUrl || '',
            originalFileName: packingSlip.fileName || ''
        }, () => {
            // Load all purchased parts for the vendor in edit mode
            if (packingSlip.vendorName) {
                this.loadAllPurchasedPartsForVendor(packingSlip.vendorName);
            }
        });
    };

    openEditMultiBoxShipment = (boxes, baseName, clickedBoxNumber = 1) => {
        // Prepare the data for editing a multi-box shipment
        const numberOfBoxes = boxes.length;
        const boxesSelectedParts = {};
        const boxesSelectedPartsQuantities = {};
        
        boxes.forEach((box, index) => {
            const boxNumber = index + 1;
            const selectedParts = {};
            const selectedPartsQuantities = {};
            
            box.items.forEach(item => {
                selectedParts[item.purchasedPartId] = true;
                selectedPartsQuantities[item.purchasedPartId] = item.quantityExpected;
            });
            
            boxesSelectedParts[boxNumber] = selectedParts;
            boxesSelectedPartsQuantities[boxNumber] = selectedPartsQuantities;
        });

        this.setState({
            packingSlipDrawerOpen: true,
            isEditMode: true,
            editingMultiBoxShipment: {
                baseName,
                boxes
            },
            editingBoxes: boxes,
            selectedVendor: boxes[0].vendorName,
            outstandingParts: [],
            selectedParts: {},
            selectedPartsQuantities: {},
            creatingPackingSlip: false,
            packingSlipName: baseName,
            packingSlipFiles: [],
            packingSlipTrackingNumber: boxes[0].trackingNumber || '',
            numberOfBoxes,
            currentBoxTab: Math.min(clickedBoxNumber, numberOfBoxes), // Set to the clicked box number
            boxesSelectedParts,
            boxesSelectedPartsQuantities,
            sortBy: DEFAULT_OUTSTANDING_PARTS_SORT_BY,
            sortDirection: DEFAULT_OUTSTANDING_PARTS_SORT_DIRECTION,
            originalFileUrl: boxes[0].fileUrl || '',
            originalFileName: boxes[0].fileName || ''
        }, () => {
            // Load all purchased parts for the vendor in edit mode
            if (boxes[0].vendorName) {
                this.loadAllPurchasedPartsForVendor(boxes[0].vendorName);
            }
        });
    };

    openEditBoxFromMultiBoxView = (box) => {
        // When editing from the multi-box shipment view, open the edit for the entire shipment
        const baseNameMatch = box.name.match(/^(.+)\s-\sBox\s\d+$/);
        if (baseNameMatch) {
            const baseName = baseNameMatch[1];
            const relatedBoxes = this.state.packingSlips.filter(ps => 
                ps.name.startsWith(baseName + ' - Box ')
            ).sort((a, b) => {
                const aBoxNum = parseInt(a.name.match(/Box\s(\d+)$/)?.[1] || 0);
                const bBoxNum = parseInt(b.name.match(/Box\s(\d+)$/)?.[1] || 0);
                return aBoxNum - bBoxNum;
            });
            
            // Find the box number of the clicked box to set the correct tab
            const clickedBoxNumber = parseInt(box.name.match(/Box\s(\d+)$/)?.[1] || 1);
            
            this.openEditMultiBoxShipment(relatedBoxes, baseName, clickedBoxNumber);
        }
    };

         // Receiving methods for multi-box shipments
     startReceivingBox = (boxId) => {
         const box = this.state.selectedMultiBoxShipment.boxes.find(b => b.id === boxId);
         if (!box) return;
 
         // Initialize receiving quantities with outstanding quantities (same logic as PackingSlipDetails.js)
         const receivingQuantities = {};
         box.items.forEach(item => {
             receivingQuantities[item.id] = Math.max(0, item.quantityExpected - item.quantityReceived);
         });
 
         this.setState({
             receivingBoxId: boxId,
             receivingQuantities,
             isReceiving: false
         });
     };

    cancelReceivingBox = () => {
        this.setState({
            receivingBoxId: null,
            receivingQuantities: {},
            isReceiving: false
        });
    };

    updateReceivingQuantity = (itemId, quantity) => {
        this.setState(prevState => ({
            receivingQuantities: {
                ...prevState.receivingQuantities,
                [itemId]: quantity
            }
        }));
    };

    fullyReceiveBox = async (boxId) => {
        const box = this.state.selectedMultiBoxShipment.boxes.find(b => b.id === boxId);
        if (!box) return;

        this.setState({ isReceiving: true });

        try {
            const partService = ServiceLocator.Instance.getPartService();
            const userId = AuthenticationService.getInstance().getUserId();
            
            // Calculate outstanding quantities for all items
            const outstandingQuantities = {};
            let hasOutstandingItems = false;
            
            if (box.items && box.items.length > 0) {
                box.items.forEach(item => {
                    const outstanding = Math.max(0, (item.quantityExpected || 0) - (item.quantityReceived || 0));
                    if (outstanding > 0) {
                        outstandingQuantities[item.id] = outstanding;
                        hasOutstandingItems = true;
                    }
                });
            }
            
            // Always use receivePackingSlipWithQuantities if there are outstanding items
            // This handles cases where new lines were added to a previously received packing slip
            if (hasOutstandingItems) {
                await partService.receivePackingSlipWithQuantities(boxId, outstandingQuantities, userId);
            } else {
                // For packing slips with no outstanding items, use the original method
                await partService.receivePurchaseOrderPackingSlip(boxId, userId);
            }
            
            // Refresh the data
            await this.loadPackingSlips();
            this.refreshMultiBoxShipmentData();
            
            window.$alertSuccess(`Successfully received items for ${box.name}`);
            
            this.setState({
                receivingBoxId: null,
                receivingQuantities: {},
                isReceiving: false
            });
        } catch (error) {
            console.error('Error receiving packing slip:', error);
            const errorMessage = error.response?.data?.error || 'Error receiving packing slip';
            window.$alertError(errorMessage);
            this.setState({ isReceiving: false });
        }
    };

         submitReceiving = async (boxId, quantities = null) => {
         const box = this.state.selectedMultiBoxShipment.boxes.find(b => b.id === boxId);
         if (!box) return;
 
         // Parse the receiving quantities to get actual numbers
         const quantitiesToSubmit = quantities || Object.keys(this.state.receivingQuantities).reduce((acc, itemId) => {
             acc[itemId] = parseNumberInputValue(this.state.receivingQuantities[itemId]);
             return acc;
         }, {});
         
         this.setState({ isReceiving: true });
 
         try {
             const partService = ServiceLocator.Instance.getPartService();
             const userId = AuthenticationService.getInstance().getUserId();
             
             await partService.receivePackingSlipWithQuantities(boxId, quantitiesToSubmit, userId);
             
             // Refresh the data
             await this.loadPackingSlips();
             this.refreshMultiBoxShipmentData();
             
             window.$alertSuccess(`Successfully received items for ${box.name}`);
             
             this.setState({
                 receivingBoxId: null,
                 receivingQuantities: {},
                 isReceiving: false
             });
         } catch (error) {
             console.error('Error receiving packing slip:', error);
             const errorMessage = error.response?.data?.error || 'Error receiving packing slip';
             window.$alertError(errorMessage);
             this.setState({ isReceiving: false });
         }
     };

    // Refresh the currently open multi-box shipment data
    refreshMultiBoxShipmentData = () => {
        if (!this.state.selectedMultiBoxShipment) return;

        const currentBaseName = this.state.selectedMultiBoxShipment.baseName;
        const {multiBoxShipments} = this.detectMultiBoxShipments(this.state.packingSlips);
        const updatedShipment = multiBoxShipments.find(shipment => shipment.baseName === currentBaseName);

        if (updatedShipment) {
            // If the shipment still exists (has boxes), update it
            this.setState({
                selectedMultiBoxShipment: updatedShipment
            });
            
            // Ensure all purchased parts are loaded for this vendor
            if (updatedShipment.vendorName && (!this.state.outstandingParts || this.state.outstandingParts.length === 0)) {
                this.loadAllPurchasedPartsForVendor(updatedShipment.vendorName);
            }
        } else {
            // If no boxes remain for this shipment, close the view
            this.closeMultiBoxShipmentView();
        }
    };

    // Delete entire shipment (all packing slips in the shipment)
    deleteEntireShipment = async () => {
        if (!this.state.selectedMultiBoxShipment) return;

        const shipment = this.state.selectedMultiBoxShipment;
        const boxCount = shipment.boxes.length;
        
        const confirm = window.confirm(
            `Are you sure you want to delete this entire shipment (${boxCount} ${boxCount === 1 ? 'box' : 'boxes'})? This action cannot be undone.`
        );
        
        if (!confirm) return;

        this.setState({ deletingEntireShipment: true });
        
        try {
            const partService = ServiceLocator.Instance.getPartService();
            
            // Delete all packing slips asynchronously
            const deletePromises = shipment.boxes.map(async (box) => {
                try {
                    await partService.deletePackingSlip(box.id);
                    return { boxId: box.id, boxName: box.name, success: true };
                } catch (error) {
                    console.error(`Error deleting packing slip ${box.id}:`, error);
                    // Return error info so we can handle partial failures
                    const errorMessage = error.response?.status === 400 || 
                                       error.response?.data?.error?.includes('Cannot delete a packing slip that has already been received')
                                       ? 'Cannot delete a box that has already been received'
                                       : 'Error deleting box';
                    return { boxId: box.id, boxName: box.name, success: false, error: errorMessage };
                }
            });
            
            const results = await Promise.all(deletePromises);
            
            // Check for any errors
            const errors = results.filter(result => !result.success);
            const successes = results.filter(result => result.success);
            
            if (errors.length > 0) {
                const errorBoxNames = errors.map(e => e.boxName || `Box ${e.boxId}`).join(', ');
                const errorMessage = errors[0].error || 'Error deleting boxes';
                window.$alertError(`${errorMessage}: ${errorBoxNames}`);
                
                // If some boxes were deleted successfully, refresh the view
                if (successes.length > 0) {
                    await this.loadPackingSlips();
                    this.refreshMultiBoxShipmentData();
                }
            } else {
                // All deletions succeeded
                window.$alertSuccess(`Entire shipment deleted successfully (${boxCount} ${boxCount === 1 ? 'box' : 'boxes'})`);
                
                // Close the multi-box shipment view
                this.closeMultiBoxShipmentView();
                
                // Refresh the packing slips list
                await this.loadPackingSlips();
            }
        } catch (error) {
            console.error('Error deleting entire shipment:', error);
            window.$alertError('Error deleting entire shipment');
        } finally {
            this.setState({ deletingEntireShipment: false });
        }
    };

    // Get part information for a packing slip item
    getPartInfo = (item) => {        
        // First try to find the purchased part from allPurchasedParts (for complete lookup)
        if (item.purchasedPartId && this.state.allPurchasedParts && this.state.allPurchasedParts.length > 0) {
            const purchasedPart = this.state.allPurchasedParts.find(p => p.id === item.purchasedPartId);
            
            if (purchasedPart) {
                // Look up individual part by partId
                if (purchasedPart.partId && this.state.parts && this.state.parts.length > 0) {
                    const partData = this.state.parts.find(p => p.part && p.part.id === purchasedPart.partId);
                    if (partData?.part?.partId) {
                        const revision = partData.part.revision ? ` rev ${partData.part.revision}` : '';
                        return {
                            partId: partData.part.partId + revision,
                            description: partData.part.description || 'Unknown Description'
                        };
                    }
                }
                
                // Look up part family by partFamilyId  
                if (purchasedPart.partFamilyId && this.state.partFamilies && this.state.partFamilies.length > 0) {
                    const family = this.state.partFamilies.find(f => f.id === purchasedPart.partFamilyId);
                    if (family) {
                        return {
                            partId: family.partFamilyId,
                            description: family.description
                        };
                    }
                }
                
                // Use part number and description from purchased part if available
                if (purchasedPart.partNumber && purchasedPart.partDescription) {
                    return {
                        partId: purchasedPart.partNumber,
                        description: purchasedPart.partDescription
                    };
                }
                
                // Use part family name if available
                if (purchasedPart.partFamilyName) {
                    return {
                        partId: purchasedPart.partFamilyName,
                        description: purchasedPart.partFamilyName
                    };
                }
            }
        }
        
        // Fallback: try to find the purchased part from outstandingParts
        if (item.purchasedPartId && this.state.outstandingParts && this.state.outstandingParts.length > 0) {
            const purchasedPart = this.state.outstandingParts.find(p => p.id === item.purchasedPartId);
            
            if (purchasedPart) {
                // Look up individual part by partId
                if (purchasedPart.partId && this.state.parts && this.state.parts.length > 0) {
                    const partData = this.state.parts.find(p => p.part && p.part.id === purchasedPart.partId);
                    if (partData?.part?.partId) {
                        const revision = partData.part.revision ? ` rev ${partData.part.revision}` : '';
                        return {
                            partId: partData.part.partId + revision,
                            description: partData.part.description || 'Unknown Description'
                        };
                    }
                }
                
                // Look up part family by partFamilyId  
                if (purchasedPart.partFamilyId && this.state.partFamilies && this.state.partFamilies.length > 0) {
                    const family = this.state.partFamilies.find(f => f.id === purchasedPart.partFamilyId);
                    if (family) {
                        return {
                            partId: family.partFamilyId,
                            description: family.description
                        };
                    }
                }
                
                // Use part number and description from purchased part if available
                if (purchasedPart.partNumber && purchasedPart.partDescription) {
                    return {
                        partId: purchasedPart.partNumber,
                        description: purchasedPart.partDescription
                    };
                }
                
                // Use part family name if available
                if (purchasedPart.partFamilyName) {
                    return {
                        partId: purchasedPart.partFamilyName,
                        description: purchasedPart.partFamilyName
                    };
                }
            }
        }
        
        // Legacy support: Check if we have the purchased part data directly
        if (item.purchasedPart) {
            // Look up individual part by partId
            if (item.purchasedPart.partId && this.state.parts && this.state.parts.length > 0) {
                const partData = this.state.parts.find(p => p.part && p.part.id === item.purchasedPart.partId);
                if (partData?.part?.partId) {
                    const revision = partData.part.revision ? ` rev ${partData.part.revision}` : '';
                    return {
                        partId: partData.part.partId + revision,
                        description: partData.part.description || 'Unknown Description'
                    };
                }
            }
            
            // Look up part family by partFamilyId  
            if (item.purchasedPart.partFamilyId && this.state.partFamilies && this.state.partFamilies.length > 0) {
                const family = this.state.partFamilies.find(f => f.id === item.purchasedPart.partFamilyId);
                if (family) {
                    return {
                        partId: family.partFamilyId,
                        description: family.description
                    };
                }
            }
            
            // Check if the purchased part has direct part description
            if (item.purchasedPart.partDescription) {
                return {
                    partId: 'PP-' + item.purchasedPart.id.substring(0, 8),
                    description: item.purchasedPart.partDescription
                };
            }
            
            // Fallback to company name if available
            if (item.purchasedPart.companyName) {
                return {
                    partId: 'VENDOR-' + item.purchasedPart.id.substring(0, 8),
                    description: item.purchasedPart.companyName
                };
            }
        }
        
        // Final fallback
        console.log('No part info found, using fallback for item:', item);
        console.log('Available purchased parts:', this.state.allPurchasedParts);
        return {
            partId: 'Unknown',
            description: 'Unknown Part'
        };
    };

    getShipmentSummaryRows = (shipment) => {
        if (!shipment?.boxes?.length) return [];

        const summaryByPart = {};

        shipment.boxes.forEach((box, index) => {
            const boxNumber = index + 1;
            (box.items || []).forEach((item) => {
                const partKey = item.purchasedPartId || item.id;
                if (!summaryByPart[partKey]) {
                    const partInfo = this.getPartInfo(item);
                    const purchasedPartData = this.state.allPurchasedParts.find(p => p.id === item.purchasedPartId);
                    summaryByPart[partKey] = {
                        key: partKey,
                        partNumber: partInfo.partId,
                        description: partInfo.description,
                        purchaseOrderNumber: purchasedPartData?.purchaseOrderNumber || '-',
                        quantityExpected: 0,
                        quantityReceived: 0,
                        boxNumbers: new Set()
                    };
                }

                summaryByPart[partKey].quantityExpected += item.quantityExpected || 0;
                summaryByPart[partKey].quantityReceived += item.quantityReceived || 0;
                summaryByPart[partKey].boxNumbers.add(boxNumber);
            });
        });

        const getSortPartNumber = (partNumber) => {
            // Sort by the base part number (without revision suffix) for cleaner ordering.
            return String(partNumber || '').split(' rev ')[0].trim();
        };

        return Object.values(summaryByPart)
            .map(row => ({
                ...row,
                boxCount: row.boxNumbers.size,
                quantityOutstanding: Math.max(0, row.quantityExpected - row.quantityReceived)
            }))
            .sort((a, b) => getSortPartNumber(a.partNumber).localeCompare(getSortPartNumber(b.partNumber), undefined, { numeric: true, sensitivity: 'base' }));
    };

    getFilteredPackingSlips = () => {
        let filtered = this.state.packingSlips;
        
        // Apply search filter
        if (this.state.searchTerm) {
            const searchTerm = this.state.searchTerm.toLowerCase();
            filtered = filtered.filter(packingSlip => 
                (packingSlip.name && packingSlip.name.toLowerCase().includes(searchTerm)) ||
                (packingSlip.vendorName && packingSlip.vendorName.toLowerCase().includes(searchTerm)) ||
                (packingSlip.trackingNumber && packingSlip.trackingNumber.toLowerCase().includes(searchTerm)) ||
                (packingSlip.items && packingSlip.items.some(item => 
                    item.part && item.part.partId && item.part.partId.toLowerCase().includes(searchTerm)
                ))
            );
        }
        
        // Apply unreceived filter
        if (this.state.showOnlyUnreceived) {
            filtered = filtered.filter(packingSlip => !isPackingSlipFullyReceived(packingSlip));
        }
        
        // Detect multi-box shipments and combine with standalone slips
        const { multiBoxShipments, standaloneSlips } = this.detectMultiBoxShipments(filtered);
        const combinedResults = [...multiBoxShipments, ...standaloneSlips];
        
        // Sort results
        combinedResults.sort((a, b) => {
            let valueA, valueB;
            
            switch (this.state.packingSlipsSortBy) {
                case 'name':
                    valueA = a.name || a.baseName || '';
                    valueB = b.name || b.baseName || '';
                    break;
                case 'vendor':
                    valueA = a.vendorName || '';
                    valueB = b.vendorName || '';
                    break;
                case 'dateCreated':
                    valueA = new Date(a.dateCreated);
                    valueB = new Date(b.dateCreated);
                    break;
                case 'trackingNumber':
                    valueA = a.trackingNumber || '';
                    valueB = b.trackingNumber || '';
                    break;
                case 'dateReceived':
                    valueA = a.dateReceived ? new Date(a.dateReceived) : new Date(0);
                    valueB = b.dateReceived ? new Date(b.dateReceived) : new Date(0);
                    break;
                default:
                    valueA = a.dateCreated;
                    valueB = b.dateCreated;
            }
            
            if (valueA < valueB) return this.state.packingSlipsSortDirection === 'asc' ? -1 : 1;
            if (valueA > valueB) return this.state.packingSlipsSortDirection === 'asc' ? 1 : -1;
            return 0;
        });
        
        return combinedResults;
    };

    handleVendorSelect = (vendorName) => {
        this.setState({selectedVendor: vendorName});
        if (vendorName) {
            this.loadOutstandingParts(vendorName);
        } else {
            this.setState({outstandingParts: [], selectedParts: {}, selectedPartsQuantities: {}});
        }
    };

    handlePartSelect = (partId, isSelected) => {
        this.setState(prevState => {
            const newSelectedParts = {
                ...prevState.selectedParts,
                [partId]: isSelected
            };
            
            // If deselecting, remove from quantities as well
            const newSelectedPartsQuantities = {...prevState.selectedPartsQuantities};
            if (!isSelected) {
                delete newSelectedPartsQuantities[partId];
            } else {
                // Set default quantity to available quantity
                const part = this.state.outstandingParts.find(p => p.id === partId);
                if (part) {
                    newSelectedPartsQuantities[partId] = part.quantityAvailable;
                }
            }
            
            return {
                selectedParts: newSelectedParts,
                selectedPartsQuantities: newSelectedPartsQuantities
            };
        }, () => {
            // Autofocus the quantity input field for the newly selected part
            if (isSelected) {
                setTimeout(() => {
                    const quantityInput = document.querySelector(`input[data-part-id-packing="${partId}"]`);
                    if (quantityInput) {
                        quantityInput.focus();
                        quantityInput.select();
                    }
                }, 100);
            }
        });
    };

    handlePartQuantityChange = (partId, quantity) => {
        this.setState(prevState => ({
            selectedPartsQuantities: {
                ...prevState.selectedPartsQuantities,
                [partId]: quantity
            }
        }));
    };

    handleSelectAll = () => {
        const currentBox = this.state.currentBoxTab;
        const boxesSelectedParts = { ...this.state.boxesSelectedParts };
        const boxesSelectedPartsQuantities = { ...this.state.boxesSelectedPartsQuantities };
        
        // Only select parts that match the search term (not affected by showSelected filter)
        let partsToSelect = this.state.outstandingParts;
        const searchTerms = this.getPartsSearchTerms();
        if (searchTerms.length > 0) {
            partsToSelect = partsToSelect.filter(part => this.partMatchesPartsSearchTerm(part, searchTerms));
        }
        
        partsToSelect.forEach(part => {
            boxesSelectedParts[currentBox][part.id] = true;
            boxesSelectedPartsQuantities[currentBox][part.id] = this.getAdjustedAvailableQuantity(part.id);
        });
        
        this.setState({boxesSelectedParts, boxesSelectedPartsQuantities});
    };

    handleSelectNone = () => {
        const currentBox = this.state.currentBoxTab;
        const boxesSelectedParts = { ...this.state.boxesSelectedParts };
        const boxesSelectedPartsQuantities = { ...this.state.boxesSelectedPartsQuantities };
        
        boxesSelectedParts[currentBox] = {};
        boxesSelectedPartsQuantities[currentBox] = {};
        
        this.setState({ boxesSelectedParts, boxesSelectedPartsQuantities });
    };

    handleShowSelectedToggle = () => {
        this.setState(prevState => ({ showSelected: !prevState.showSelected }));
    };

    handlePartsSearch = (event) => {
        this.setState({ partsSearchTerm: event.target.value, showSelected: false });
    };

    getFirstPurchaseOrderNumber = (packingSlip) => {
        if (!packingSlip.items || packingSlip.items.length === 0) return '';
        
        // Find the first item that has a purchase order number
        for (const item of packingSlip.items) {
            if (item.purchasedPart && item.purchasedPart.purchaseOrderNumber) {
                return item.purchasedPart.purchaseOrderNumber.toString();
            }
        }
        return '';
    };

    getFilteredParts = () => {
        if (!this.state.partsSearchTerm) return this.state.parts;
        
        const searchTerms = this.state.partsSearchTerm.split(' ').filter(term => term.trim() !== '');
        if (searchTerms.length === 0) return this.state.parts;
        
        return this.state.parts.filter(part => {
            // All search terms must match at least one field
            return searchTerms.every(term => {
                const searchTerm = term.toLowerCase();
                return (part.partNumber && part.partNumber.toLowerCase().includes(searchTerm)) ||
                       (part.partFamilyName && part.partFamilyName.toLowerCase().includes(searchTerm)) ||
                       (part.purchaseOrderNumber && part.purchaseOrderNumber.toString().toLowerCase().includes(searchTerm)) ||
                       (part.partDescription && part.partDescription.toLowerCase().includes(searchTerm));
            });
        });
    };

    partMatchesPartsSearchTerm = (part, searchTerms) => {
        return searchTerms.every(term => {
            const searchTerm = term.toLowerCase();
            return (part.partNumber && part.partNumber.toLowerCase().includes(searchTerm)) ||
                   (part.partFamilyName && part.partFamilyName.toLowerCase().includes(searchTerm)) ||
                   (part.purchaseOrderNumber && part.purchaseOrderNumber.toString().toLowerCase().includes(searchTerm)) ||
                   (part.partDescription && part.partDescription.toLowerCase().includes(searchTerm));
        });
    };

    getPartsSearchTerms = () => {
        return (this.state.partsSearchTerm || '').split(' ').filter(term => term.trim() !== '');
    };

    filterOutstandingParts = (parts) => {
        let filteredParts = parts;
        const searchTerms = this.getPartsSearchTerms();

        if (searchTerms.length > 0) {
            filteredParts = filteredParts.filter(part => this.partMatchesPartsSearchTerm(part, searchTerms));
        }

        // Then filter by selected items if showSelected is enabled
        if (this.state.showSelected) {
            const currentBox = this.state.currentBoxTab;
            filteredParts = filteredParts.filter(part => this.state.boxesSelectedParts[currentBox]?.[part.id]);
        }

        // When search or "Show selected" is active, return the filtered list as-is.
        // Otherwise edit mode would re-add every slip line and defeat the filter.
        if (searchTerms.length > 0 || this.state.showSelected) {
            return filteredParts;
        }

        const currentlySelectedPartIds = new Set();
        if (this.state.isEditMode) {
            if (this.state.editingPackingSlipId) {
                const editingSlip = this.state.packingSlips.find(ps => ps.id === this.state.editingPackingSlipId);
                if (editingSlip && editingSlip.items) {
                    editingSlip.items.forEach(item => {
                        currentlySelectedPartIds.add(item.purchasedPartId);
                    });
                }
            } else if (this.state.editingMultiBoxShipment) {
                this.state.editingBoxes.forEach(box => {
                    if (box.items) {
                        box.items.forEach(item => {
                            currentlySelectedPartIds.add(item.purchasedPartId);
                        });
                    }
                });
            }
        }

        const combinedParts = [...filteredParts];
        parts.filter(part => currentlySelectedPartIds.has(part.id)).forEach(part => {
            if (!combinedParts.find(p => p.id === part.id)) {
                combinedParts.push(part);
            }
        });

        return combinedParts;
    };

    getEditingPackingSlipExcludeIds = () => {
        const ids = [];

        if (this.state.editingBoxes?.length) {
            this.state.editingBoxes.forEach((box) => {
                if (box.id) ids.push(box.id);
            });
        } else if (this.state.editingPackingSlipId) {
            ids.push(this.state.editingPackingSlipId);
        }

        return ids;
    };

    validatePackingSlipNameUnique = () => {
        const conflict = findPackingSlipBaseNameConflict(
            this.state.packingSlipName,
            this.state.packingSlips,
            this.state.isEditMode ? this.getEditingPackingSlipExcludeIds() : []
        );

        if (!conflict) return true;

        const existingLabel = conflict.existingSlip.name;
        window.$alertWarning(
            `A packing slip shipment named "${conflict.proposedBaseName}" already exists (for example "${existingLabel}"). ` +
            'Choose a different name or edit the existing shipment.'
        );

        return false;
    };

    getPrimaryPackingSlipIdForFileUpload = (updatedPackingSlips, createdPackingSlips, existingBoxes) => {
        const box1Name = this.state.numberOfBoxes > 1
            ? `${this.state.packingSlipName} - Box 1`
            : this.state.packingSlipName;

        const updated = updatedPackingSlips.find(slip => slip.name === box1Name);
        if (updated) return updated.id;

        const created = createdPackingSlips.find(slip => slip.name === box1Name);
        if (created) return created.id;

        const existing = (existingBoxes || []).find(box => box.name === box1Name);
        if (existing) return existing.id;

        return this.state.editingPackingSlipId;
    };

    uploadEditedPackingSlipFiles = async (primaryPackingSlipId, allBoxIds = []) => {
        if (!this.state.packingSlipFiles?.length || !primaryPackingSlipId) return;

        const partService = ServiceLocator.Instance.getPartService();
        const file = this.state.packingSlipFiles[0];

        try {
            await partService.uploadPackingSlipFile(primaryPackingSlipId, file);

            for (const boxId of allBoxIds) {
                if (boxId !== primaryPackingSlipId) {
                    try {
                        await partService.clonePackingSlipFileFromOtherSlip(boxId, primaryPackingSlipId);
                    } catch (error) {
                        console.error('Error cloning uploaded file to box:', error);
                    }
                }
            }
        } catch (error) {
            console.error('Error uploading packing slip file:', error);
            window.$alertWarning('Packing slip saved, but the file upload failed. Try uploading again.');
        }
    };

    handleCreatePackingSlip = async () => {
        if (!this.state.packingSlipName.trim()) {
            window.$alertWarning('Please enter a packing slip name');
            return;
        }

        if (!this.validatePackingSlipNameUnique()) {
            return;
        }

        // Check for quantity validation errors first
        const validationErrors = this.getValidationErrors();
        if (validationErrors.length > 0 && !this.state.overrideValidation) {
            window.$alertWarning(`Cannot ${this.state.isEditMode ? 'update' : 'create'} packing slip: Some parts exceed available quantities. Please review the highlighted errors and adjust quantities before proceeding, or enable override to save anyway.`);
            return;
        }

        // If override is enabled, show confirmation dialog
        if (validationErrors.length > 0 && this.state.overrideValidation) {
            const confirmed = window.confirm(
                `You are about to ${this.state.isEditMode ? 'update' : 'create'} a packing slip with quantities that exceed available orders.\n\n` +
                `Affected parts:\n${validationErrors.map(error => `- ${error.partNumber} rev ${error.revision}: ${error.message}`).join('\n')}`
            );
            if (!confirmed) {
                return;
            }
        }

        // Validate that at least one box has selected parts
        let totalSelectedParts = 0;
        const boxesToProcess = [];
        

        
        for (let boxNumber = 1; boxNumber <= this.state.numberOfBoxes; boxNumber++) {
            const boxSelectedParts = this.state.boxesSelectedParts[boxNumber] || {};
            const boxSelectedQuantities = this.state.boxesSelectedPartsQuantities[boxNumber] || {};
            const selectedPartIds = Object.keys(boxSelectedParts).filter(id => boxSelectedParts[id]);
            

            
            if (selectedPartIds.length > 0) {
                totalSelectedParts += selectedPartIds.length;
                
                // Validate quantities for this box
                for (const partId of selectedPartIds) {
                    const quantity = parseNumberInputValue(boxSelectedQuantities[partId]);
                    const part = this.state.outstandingParts.find(p => p.id === partId);
                    
                    if (!part) continue;
                    
                    const availableQuantityForValidation = this.getAvailableQuantityForValidation(partId);
                    if (!quantity || quantity <= 0 || (quantity > availableQuantityForValidation && !this.state.overrideValidation)) {
                        window.$alertWarning(`Box ${boxNumber}: Please enter a valid quantity for ${part.partNumber || part.partFamilyName || 'selected part'} (1-${availableQuantityForValidation})`);
                        return;
                    }
                }

                boxesToProcess.push({
                    boxNumber,
                    selectedPartIds,
                    quantities: boxSelectedQuantities
                });
            }
        }
        

        
        if (totalSelectedParts === 0) {
            window.$alertWarning('Please select at least one part in any box');
            return;
        }

        // Validate that total quantities across all boxes don't exceed available quantities
        const totalQuantitiesByPart = {};
        boxesToProcess.forEach(box => {
            box.selectedPartIds.forEach(partId => {
                totalQuantitiesByPart[partId] = (totalQuantitiesByPart[partId] || 0) + parseNumberInputValue(box.quantities[partId]);
            });
        });

        for (const [partId, totalQuantity] of Object.entries(totalQuantitiesByPart)) {
            const part = this.state.outstandingParts.find(p => p.id === partId);
            const originalAvailableQuantity = this.getOriginalAvailableQuantity(partId);
            if (part && totalQuantity > originalAvailableQuantity && !this.state.overrideValidation) {
                window.$alertWarning(`Total quantity for ${part.partNumber || part.partFamilyName || 'selected part'} across all boxes (${totalQuantity}) exceeds available quantity (${originalAvailableQuantity}). Enable override to save anyway.`);
                return;
            }
        }

        this.setState({creatingPackingSlip: true});

        try {
            const partService = ServiceLocator.Instance.getPartService();
            
            if (this.state.isEditMode) {
                // Handle edit mode
                if (this.state.editingMultiBoxShipment || (this.state.isEditMode && this.state.numberOfBoxes > 1)) {
                    // Editing a multi-box shipment (either started as multi-box or single box that became multi-box)
                    // Note: When a single box becomes multi-box, we need to handle the original single box properly
                    const updatedPackingSlips = [];
                    const createdPackingSlips = [];
                    const deletedPackingSlipIds = [];
                    
                    // First, identify which existing boxes should be deleted (boxes that are no longer in the current edit)
                    const currentBoxNames = boxesToProcess.map(box => 
                        this.state.numberOfBoxes > 1 
                            ? `${this.state.packingSlipName} - Box ${box.boxNumber}`
                            : this.state.packingSlipName
                    );
                    
                    // Get the existing boxes to work with
                    let existingBoxes = this.state.editingBoxes || [];
                    
                    // If we don't have editingBoxes but have an editingPackingSlipId, 
                    // we need to find the actual packing slip from the state
                    if (existingBoxes.length === 0 && this.state.editingPackingSlipId) {
                        const actualPackingSlip = this.state.packingSlips.find(ps => ps.id === this.state.editingPackingSlipId);
                        if (actualPackingSlip) {
                            existingBoxes = [{
                                id: actualPackingSlip.id,
                                name: actualPackingSlip.name
                            }];
                        } else {
                            // Fallback to the current name if we can't find the actual packing slip
                            existingBoxes = [{
                                id: this.state.editingPackingSlipId,
                                name: this.state.packingSlipName
                            }];
                        }
                    }
                    
                    // For multi-box shipments, we need to match by box number rather than exact name
                    // since the base name might have changed during editing
                    existingBoxes.forEach(existingBox => {
                        if (this.state.numberOfBoxes > 1) {
                            // For multi-box shipments, extract box number from existing box name
                            const boxNumberMatch = existingBox.name.match(/Box\s+(\d+)$/);
                            if (boxNumberMatch) {
                                const existingBoxNumber = parseInt(boxNumberMatch[1]);
                                const hasMatchingBox = boxesToProcess.some(box => box.boxNumber === existingBoxNumber);
                                if (!hasMatchingBox) {
                                    deletedPackingSlipIds.push(existingBox.id);
                                }
                            } else {
                                // If we can't parse the box number, check if the name matches any current box name
                                if (!currentBoxNames.includes(existingBox.name)) {
                                    deletedPackingSlipIds.push(existingBox.id);
                                }
                            }
                        } else {
                            // For single box, check if the name matches or if it's a "Box 1" that should be updated
                            const shouldDelete = !currentBoxNames.includes(existingBox.name) && 
                                               !(existingBox.name === `${this.state.packingSlipName} - Box 1` && boxesToProcess.length === 1 && boxesToProcess[0].boxNumber === 1);
                            if (shouldDelete) {
                                deletedPackingSlipIds.push(existingBox.id);
                            }
                        }
                    });
                    
                    // Delete removed boxes
                    for (const boxId of deletedPackingSlipIds) {
                        try {
                            await partService.deletePackingSlip(boxId);
                            console.log(`Successfully deleted packing slip: ${boxId}`);
                        } catch (error) {
                            console.error('Error deleting packing slip:', error);
                            // Continue with other operations even if deletion fails
                        }
                    }
                    
                    // Process each box in the current edit
                    for (const box of boxesToProcess) {
                        const boxName = this.state.numberOfBoxes > 1 
                            ? `${this.state.packingSlipName} - Box ${box.boxNumber}`
                            : this.state.packingSlipName;
                            
                        // Find existing box by matching box number for multi-box shipments
                        let existingBox;
                        if (this.state.numberOfBoxes > 1) {
                            // For multi-box shipments, find by box number
                            existingBox = existingBoxes.find(b => {
                                const boxNumberMatch = b.name.match(/Box\s+(\d+)$/);
                                if (boxNumberMatch) {
                                    const existingBoxNumber = parseInt(boxNumberMatch[1]);
                                    return existingBoxNumber === box.boxNumber;
                                }
                                return false;
                            });
                            
                            // If this is box 1 and we started with a single box, also check for the original single box
                            if (!existingBox && box.boxNumber === 1 && this.state.editingPackingSlipId && !this.state.editingMultiBoxShipment) {
                                existingBox = existingBoxes.find(b => 
                                    b.id === this.state.editingPackingSlipId
                                );
                            }
                        } else {
                            // For single box, find by exact name match or by box number suffix
                            existingBox = existingBoxes.find(b => 
                                b.name === boxName ||
                                b.name === this.state.packingSlipName ||
                                (box.boxNumber === 1 && b.name === `${this.state.packingSlipName} - Box 1`)
                            );
                        }
                        
                        if (existingBox) {
                            // Update existing box
                            // Find the existing box data to preserve item IDs where possible
                            const existingBoxData = this.state.packingSlips.find(ps => ps.id === existingBox.id);
                            const updatedPackingSlip = {
                                id: existingBox.id,
                                name: boxName,
                                trackingNumber: this.state.packingSlipTrackingNumber || null,
                                vendorName: this.state.selectedVendor,
                                dateCreated: existingBoxData?.dateCreated || toUtcForStorage(new Date()),
                                isReceived: existingBoxData?.isReceived || false,
                                userId: existingBoxData?.userId || this.state.user.id,
                                items: box.selectedPartIds.map(partId => {
                                    // Try to find existing item with same purchasedPartId to preserve ID
                                    const existingItem = existingBoxData?.items?.find(item => item.purchasedPartId === partId);
                                    return {
                                        id: existingItem?.id || uuid(), // Use existing ID if available, otherwise generate new
                                        purchasedPartId: partId,
                                        quantityExpected: parseNumberInputValue(box.quantities[partId]),
                                        quantityReceived: existingItem?.quantityReceived || 0 // Preserve received quantity
                                    };
                                })
                            };
                            

                            console.log('Sending update payload for packing slip:', existingBox.id, updatedPackingSlip);
                            console.log('Raw quantities for this box:', box.quantities);
                            console.log('Parsed quantities for this box:', box.selectedPartIds.map(partId => ({
                                partId,
                                rawValue: box.quantities[partId],
                                parsedValue: parseNumberInputValue(box.quantities[partId])
                            })));
                            try {
                                // First update the packing slip header
                                const updateResult = await partService.updatePackingSlip(updatedPackingSlip);
                                console.log('API response for packing slip update:', updateResult);
                                
                                // Handle line items: delete removed items, update existing items, create new items
                                const existingItemIds = new Set(existingBoxData?.items?.map(item => item.id) || []);
                                const newItemIds = new Set(updatedPackingSlip.items.map(item => item.id));
                                
                                // Delete items that are no longer present
                                for (const existingItem of existingBoxData?.items || []) {
                                    if (!newItemIds.has(existingItem.id)) {
                                        try {
                                            await partService.deletePackingSlipItem(existingItem.id);
                                            console.log(`Deleted line item: ${existingItem.id}`);
                                        } catch (deleteError) {
                                            console.error('Error deleting line item:', deleteError);
                                            console.error('Delete error response:', deleteError.response?.data);
                                        }
                                    }
                                }
                                
                                // Update or create each line item
                                for (const item of updatedPackingSlip.items) {
                                    try {
                                        if (existingItemIds.has(item.id)) {
                                            // Update existing item
                                            const itemUpdateResult = await partService.updatePackingSlipItem(item);
                                            console.log('Updated existing line item:', itemUpdateResult);
                                        } else {
                                            // Create new item
                                            const newItem = {
                                                packingSlipId: existingBox.id,
                                                purchasedPartId: item.purchasedPartId,
                                                quantityExpected: item.quantityExpected,
                                                quantityReceived: item.quantityReceived
                                            };
                                            const itemCreateResult = await partService.addItemToPackingSlip(newItem);
                                            console.log('Created new line item:', itemCreateResult);
                                        }
                                    } catch (itemError) {
                                        console.error('Error processing line item:', itemError);
                                        console.error('Item error response:', itemError.response?.data);
                                    }
                                }
                            } catch (error) {
                                console.error('Error updating packing slip:', error);
                                console.error('Error response:', error.response?.data);
                                throw error;
                            }
                            updatedPackingSlips.push(updatedPackingSlip);
                            console.log(`Successfully updated packing slip: ${existingBox.id}`);
                        } else {
                            // Create new box
                            const formData = new FormData();
                            
                            const packingSlipData = {
                                id: uuid(),
                                name: boxName,
                                trackingNumber: this.state.packingSlipTrackingNumber || null,
                                vendorName: this.state.selectedVendor,
                                dateCreated: toUtcForStorage(new Date()),
                                isReceived: false,
                                userId: this.state.user.id,
                                items: box.selectedPartIds.map(partId => ({
                                    id: uuid(),
                                    purchasedPartId: partId,
                                    quantityExpected: parseNumberInputValue(box.quantities[partId]),
                                    quantityReceived: 0
                                }))
                            };

                            formData.append('packingSlip', JSON.stringify(packingSlipData));

                            // Clone file from existing boxes if available
                            if (this.state.originalFileUrl && this.state.originalFileName && this.state.packingSlipFiles.length === 0) {
                                try {
                                    const createdPackingSlip = await partService.createPurchaseOrderPackingSlip(formData);
                                    await partService.clonePackingSlipFile(createdPackingSlip.id, this.state.originalFileUrl, this.state.originalFileName);
                                    createdPackingSlips.push(createdPackingSlip);
                                } catch (error) {
                                    console.error('Error creating new box with file cloning:', error);
                                    // Try without file cloning
                                    const createdPackingSlip = await partService.createPurchaseOrderPackingSlip(formData);
                                    createdPackingSlips.push(createdPackingSlip);
                                    console.log(`Successfully created new packing slip: ${createdPackingSlip.id}`);
                                }
                            } else {
                                const createdPackingSlip = await partService.createPurchaseOrderPackingSlip(formData);
                                createdPackingSlips.push(createdPackingSlip);
                                console.log(`Successfully created new packing slip: ${createdPackingSlip.id}`);
                            }
                        }
                    }
                    
                    const primaryPackingSlipId = this.getPrimaryPackingSlipIdForFileUpload(
                        updatedPackingSlips,
                        createdPackingSlips,
                        existingBoxes
                    );
                    const allBoxIds = [
                        ...updatedPackingSlips.map(slip => slip.id),
                        ...createdPackingSlips.map(slip => slip.id)
                    ];
                    await this.uploadEditedPackingSlipFiles(primaryPackingSlipId, allBoxIds);

                    this.loadPackingSlips().then(() => {
                        // Refresh the multi-box shipment view if it's open
                        if (this.state.multiBoxShipmentViewOpen && this.state.selectedMultiBoxShipment) {
                            this.refreshMultiBoxShipmentData();
                        }
                    });
                    this.handleClosePackingSlipDrawer();
                    
                    // Log summary for debugging
                    console.log(`Edit operation completed: ${updatedPackingSlips.length} updated, ${createdPackingSlips.length} created, ${deletedPackingSlipIds.length} deleted`);
                    
                    let successMessage = '';
                    if (updatedPackingSlips.length > 0) {
                        successMessage += `Updated ${updatedPackingSlips.length} packing slip${updatedPackingSlips.length > 1 ? 's' : ''}`;
                    }
                    if (createdPackingSlips.length > 0) {
                        if (successMessage) successMessage += ', ';
                        successMessage += `created ${createdPackingSlips.length} new packing slip${createdPackingSlips.length > 1 ? 's' : ''}`;
                    }
                    if (deletedPackingSlipIds.length > 0) {
                        if (successMessage) successMessage += ', ';
                        successMessage += `deleted ${deletedPackingSlipIds.length} packing slip${deletedPackingSlipIds.length > 1 ? 's' : ''}`;
                    }
                    
                    if (successMessage) {
                        window.$alertSuccess(`Successfully ${successMessage}`);
                    } else {
                        window.$alertSuccess('Packing slip updated successfully');
                    }
                } else if (this.state.editingPackingSlipId && this.state.numberOfBoxes === 1) {
                    // Editing a single packing slip (still single box)
                    // Find the existing packing slip data to preserve item IDs where possible
                    const existingPackingSlipData = this.state.packingSlips.find(ps => ps.id === this.state.editingPackingSlipId);
                    const updatedPackingSlip = {
                        id: this.state.editingPackingSlipId,
                        name: this.state.packingSlipName,
                        trackingNumber: this.state.packingSlipTrackingNumber || null,
                        vendorName: this.state.selectedVendor,
                        dateCreated: existingPackingSlipData?.dateCreated || toUtcForStorage(new Date()),
                        isReceived: existingPackingSlipData?.isReceived || false,
                        userId: existingPackingSlipData?.userId || this.state.user.id,
                        items: boxesToProcess[0].selectedPartIds.map(partId => {
                            // Try to find existing item with same purchasedPartId to preserve ID
                            const existingItem = existingPackingSlipData?.items?.find(item => item.purchasedPartId === partId);
                            return {
                                id: existingItem?.id || uuid(), // Use existing ID if available, otherwise generate new
                                purchasedPartId: partId,
                                quantityExpected: parseNumberInputValue(boxesToProcess[0].quantities[partId]),
                                quantityReceived: existingItem?.quantityReceived || 0 // Preserve received quantity
                            };
                        })
                    };
                    

                    console.log('Sending single slip update payload:', this.state.editingPackingSlipId, updatedPackingSlip);
                    console.log('Raw quantities from state:', boxesToProcess[0].quantities);
                    console.log('Parsed quantities:', boxesToProcess[0].selectedPartIds.map(partId => ({
                        partId,
                        rawValue: boxesToProcess[0].quantities[partId],
                        parsedValue: parseNumberInputValue(boxesToProcess[0].quantities[partId])
                    })));
                    try {
                        // First update the packing slip header
                        const updateResult = await partService.updatePackingSlip(updatedPackingSlip);
                        console.log('API response for single slip update:', updateResult);
                        
                        // Handle line items: delete removed items, update existing items, create new items
                        const existingItemIds = new Set(existingPackingSlipData?.items?.map(item => item.id) || []);
                        const newItemIds = new Set(updatedPackingSlip.items.map(item => item.id));
                        
                        // Delete items that are no longer present
                        for (const existingItem of existingPackingSlipData?.items || []) {
                            if (!newItemIds.has(existingItem.id)) {
                                try {
                                    await partService.deletePackingSlipItem(existingItem.id);
                                    console.log(`Deleted line item: ${existingItem.id}`);
                                } catch (deleteError) {
                                    console.error('Error deleting line item:', deleteError);
                                    console.error('Delete error response:', deleteError.response?.data);
                                }
                            }
                        }
                        
                        // Update or create each line item
                        for (const item of updatedPackingSlip.items) {
                            try {
                                if (existingItemIds.has(item.id)) {
                                    // Update existing item
                                    const itemUpdateResult = await partService.updatePackingSlipItem(item);
                                    console.log('Updated existing line item:', itemUpdateResult);
                                } else {
                                    // Create new item
                                    const newItem = {
                                        packingSlipId: this.state.editingPackingSlipId,
                                        purchasedPartId: item.purchasedPartId,
                                        quantityExpected: item.quantityExpected,
                                        quantityReceived: item.quantityReceived
                                    };
                                    const itemCreateResult = await partService.addItemToPackingSlip(newItem);
                                    console.log('Created new line item:', itemCreateResult);
                                }
                            } catch (itemError) {
                                console.error('Error processing line item:', itemError);
                                console.error('Item error response:', itemError.response?.data);
                            }
                        }
                    } catch (error) {
                        console.error('Error updating packing slip:', error);
                        console.error('Error response:', error.response?.data);
                        throw error;
                    }

                    await this.uploadEditedPackingSlipFiles(this.state.editingPackingSlipId);

                    this.loadPackingSlips().then(() => {
                        // Refresh the multi-box shipment view if it's open
                        if (this.state.multiBoxShipmentViewOpen && this.state.selectedMultiBoxShipment) {
                            this.refreshMultiBoxShipmentData();
                        }
                    });
                    this.handleClosePackingSlipDrawer();
                    window.$alertSuccess('Packing slip updated successfully');
                }
            } else {
                // Handle create mode (existing logic)
                const createdPackingSlips = [];

                // Create each box as a separate packing slip
                for (const box of boxesToProcess) {
                    const boxName = this.state.numberOfBoxes > 1 
                        ? `${this.state.packingSlipName} - Box ${box.boxNumber}`
                        : this.state.packingSlipName;

                    const formData = new FormData();
                    
                    const packingSlipData = {
                        id: uuid(),
                        name: boxName,
                        trackingNumber: this.state.packingSlipTrackingNumber || null,
                        vendorName: this.state.selectedVendor,
                        dateCreated: toUtcForStorage(new Date()),
                        isReceived: false,
                        userId: this.state.user.id,
                        items: box.selectedPartIds.map(partId => {
                            return {
                                id: uuid(),
                                purchasedPartId: partId,
                                quantityExpected: parseNumberInputValue(box.quantities[partId]),
                                quantityReceived: 0
                            };
                        })
                    };

                    formData.append('packingSlip', JSON.stringify(packingSlipData));

                    // Add file if uploaded (only for the first box, or if single box)
                    if (this.state.packingSlipFiles.length > 0 && box.boxNumber === 1) {
                        formData.append('file', this.state.packingSlipFiles[0]);
                    }

                    const createdPackingSlip = await partService.createPurchaseOrderPackingSlip(formData);
                    createdPackingSlips.push(createdPackingSlip);

                    // Clone file to all boxes in multi-box shipment
                    if (this.state.numberOfBoxes > 1) {
                        // If we have a new uploaded file and this is the first box
                        if (this.state.packingSlipFiles.length > 0 && box.boxNumber === 1) {
                            // Store the first box's file info for cloning to other boxes
                            const firstBoxId = createdPackingSlip.id;
                            box.firstBoxId = firstBoxId;
                        }
                        // If this is not the first box and we have an uploaded file, clone from first box
                        else if (this.state.packingSlipFiles.length > 0 && box.boxNumber > 1) {
                            const firstBox = boxesToProcess.find(b => b.boxNumber === 1);
                            if (firstBox && firstBox.firstBoxId) {
                                try {
                                    await partService.clonePackingSlipFileFromOtherSlip(createdPackingSlip.id, firstBox.firstBoxId);
                                } catch (error) {
                                    console.error('Error cloning file to box:', error);
                                    // Don't fail the entire operation if file cloning fails
                                }
                            }
                        }
                        // If we have an original file URL and no new upload, clone to all boxes
                        else if (this.state.originalFileUrl && this.state.originalFileName && this.state.packingSlipFiles.length === 0) {
                            try {
                                await partService.clonePackingSlipFile(createdPackingSlip.id, this.state.originalFileUrl, this.state.originalFileName);
                            } catch (error) {
                                console.error('Error cloning file:', error);
                                // Don't fail the entire operation if file cloning fails
                            }
                        }
                    } 
                    // Single box - handle file cloning as before
                    else if (this.state.originalFileUrl && this.state.originalFileName && this.state.packingSlipFiles.length === 0) {
                        try {
                            await partService.clonePackingSlipFile(createdPackingSlip.id, this.state.originalFileUrl, this.state.originalFileName);
                        } catch (error) {
                            console.error('Error cloning file:', error);
                            // Don't fail the entire operation if file cloning fails
                        }
                    }
                }

                this.loadPackingSlips();
                this.handleClosePackingSlipDrawer();
                
                if (createdPackingSlips.length > 1) {
                    window.$alertSuccess(`Successfully created ${createdPackingSlips.length} packing slips for boxes 1-${createdPackingSlips.length}`);
                } else if (createdPackingSlips.length === 1) {
                    window.$alertSuccess('Packing slip created successfully');
                }
            }
        } catch (error) {
            console.error(`Error ${this.state.isEditMode ? 'updating' : 'creating'} packing slip:`, error);
            window.$alertError(`Error ${this.state.isEditMode ? 'updating' : 'creating'} packing slip`);
        } finally {
            this.setState({creatingPackingSlip: false});
        }
    };

    openPackingSlipDetails = (packingSlip, event) => {
        followAppLink(this.props.history, `/admin/packing-slips/${packingSlip.id}`, event);
    };

    handleClosePackingSlipDetails = () => {
        this.setState({
            packingSlipDetailsOpen: false,
            selectedPackingSlipId: null
        });
    };

    handlePackingSlipDetailsUpdate = () => {
        // Refresh the packing slips list when receiving actions are updated
        this.loadPackingSlips().then(() => {
            // If we have a multi-box shipment open, refresh its data
            if (this.state.multiBoxShipmentViewOpen && this.state.selectedMultiBoxShipment) {
                this.refreshMultiBoxShipmentData();
            }
        });
    };

    download = (url, filename) => {
        const xhr = new XMLHttpRequest();
        xhr.responseType = 'blob';

        xhr.onload = function () {
            const hyperlinkElement = document.createElement("a");
            hyperlinkElement.href = URL.createObjectURL(xhr.response);
            hyperlinkElement.setAttribute("download", filename);
            hyperlinkElement.click();
        };

        xhr.open('GET', url);
        xhr.send();
    };

    render() {
        const self = this;
        if (this.state.loading) {
            return (
                <DashboardTemplate title="Packing Slips" onSyncDatabase={this.loadData}>
                    <div className="main-dashboard-root">
                        <CircularProgress/>
                    </div>
                </DashboardTemplate>
            );
        }

        const filteredPackingSlips = this.getFilteredPackingSlips();
        const paginatedPackingSlips = filteredPackingSlips.slice(
            this.state.page * this.state.rowsPerPage,
            this.state.page * this.state.rowsPerPage + this.state.rowsPerPage
        );

        const isCreatingNewSlip = this.state.packingSlipDrawerOpen;

        return (
            <DashboardTemplate title="Packing Slips" onSyncDatabase={this.loadData}>
                <div className="main-dashboard-root">
                    <div className="container" style={{maxWidth: '1600px'}}>
                        <div className="packing-slips-header">
                            {/* Left: Search Bar */}
                            <div className="packing-slips-search">
                                <TextField
                                    value={this.state.searchTerm}
                                    onChange={this.searchPackingSlips}
                                    label="Search by Name, Vendor, Tracking, or Part ID..."
                                    variant="outlined"
                                    className="packing-slips-search-input"
                                    inputRef={ref => this.searchInputRef = ref}
                                />
                            </div>
                            
                            {/* Center: Buttons - Absolutely centered */}
                            <div className="packing-slips-center-buttons">
                                <Button 
                                    variant="contained"
                                    color="primary"
                                    onClick={this.openCreatePackingSlip} 
                                    className="success-button-lg"
                                >
                                    New Packing Slip
                                </Button>
                            </div>
                            
                            {/* Right: Filter toggles */}
                            <div className="packing-slips-filters">
                                <div 
                                    className="filter-toggle"
                                    onClick={() => this.setState(prevState => ({ 
                                        showOnlyUnreceived: !prevState.showOnlyUnreceived,
                                        page: 0
                                    }))}
                                >
                                    <Checkbox
                                        checked={this.state.showOnlyUnreceived}
                                        onChange={() => {}}
                                        size="small"
                                    />
                                    <span className="filter-label">
                                        Show Only Unreceived
                                    </span>
                                </div>
                            </div>
                        </div>
                        <center>
                            <TablePagination
                                rowsPerPageOptions={[50, 100, 200, 500]}
                                colSpan={4}
                                count={filteredPackingSlips.length}
                                rowsPerPage={this.state.rowsPerPage}
                                page={this.state.page}
                                slotProps={{
                                    select: {
                                        'aria-label': 'rows per page',
                                    },
                                    actions: {
                                        showFirstButton: true,
                                        showLastButton: true,
                                        slots: {
                                            firstPageIcon: FirstPageIcon,
                                            lastPageIcon: LastPageIcon,
                                            nextPageIcon: ChevronRightIcon,
                                            backPageIcon: ChevronLeftIcon,
                                        },
                                    },
                                }}
                                onPageChange={this.handleChangePage}
                                onRowsPerPageChange={this.handleChangeRowsPerPage}
                            />
                        </center>
                        <div className="parts-table-container">
                            <table className="parts-table">
                                <thead>
                                <tr>
                                    <th 
                                        style={{minWidth: 200, cursor: 'pointer', userSelect: 'none'}}
                                        onClick={() => this.handlePackingSlipsSort('name')}
                                    >
                                        Name {this.state.packingSlipsSortBy === 'name' && (this.state.packingSlipsSortDirection === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th 
                                        style={{minWidth: 150, cursor: 'pointer', userSelect: 'none'}}
                                        onClick={() => this.handlePackingSlipsSort('vendor')}
                                    >
                                        Vendor {this.state.packingSlipsSortBy === 'vendor' && (this.state.packingSlipsSortDirection === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th 
                                        style={{minWidth: 130, cursor: 'pointer', userSelect: 'none'}}
                                        onClick={() => this.handlePackingSlipsSort('dateCreated')}
                                    >
                                        Date Created {this.state.packingSlipsSortBy === 'dateCreated' && (this.state.packingSlipsSortDirection === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th 
                                        style={{minWidth: 80, cursor: 'pointer', userSelect: 'none'}}
                                        onClick={() => this.handlePackingSlipsSort('purchaseOrderNumber')}
                                    >
                                        PO # {this.state.packingSlipsSortBy === 'purchaseOrderNumber' && (this.state.packingSlipsSortDirection === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th 
                                        style={{minWidth: 100, cursor: 'pointer', userSelect: 'none'}}
                                        onClick={() => this.handlePackingSlipsSort('trackingNumber')}
                                    >
                                        Tracking # {this.state.packingSlipsSortBy === 'trackingNumber' && (this.state.packingSlipsSortDirection === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th style={{minWidth: 80}}>Items</th>
                                    <th style={{minWidth: 105}}>Status</th>
                                    <th 
                                        style={{minWidth: 130, cursor: 'pointer', userSelect: 'none'}}
                                        onClick={() => this.handlePackingSlipsSort('dateReceived')}
                                    >
                                        Date Received {this.state.packingSlipsSortBy === 'dateReceived' && (this.state.packingSlipsSortDirection === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th style={{minWidth: 100}}>File</th>
                                </tr>
                                </thead>
                                <tbody>
                                {paginatedPackingSlips.map((item) => {
                                    if (item.type === 'multi-box-shipment') {
                                        // Render multi-box shipment row
                                        const isExpanded = this.isRowExpanded(item.id);
                                        return (
                                            <React.Fragment key={item.id}>
                                                <tr className={`multi-box-shipment-row ${item.isFullyReceived ? 'received' : item.hasAnyReceiving ? 'partially-received' : ''}`}>
                                                    <td className="text-left">
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0', marginLeft: 3 }}>
                                                            <FontAwesomeIcon 
                                                                icon={isExpanded ? faChevronDown : faChevronRight}
                                                                style={{ 
                                                                    cursor: 'pointer', 
                                                                    color: '#666',
                                                                    fontSize: '16px',
                                                                    transition: 'transform 0.2s ease'
                                                                }}
                                                                onClick={() => this.toggleRowExpansion(item.id)}
                                                            />
                                                            <a
                                                                className="multi-box-shipment-name"
                                                                href={`/admin/packing-slips/box/${item.boxes[0].id}`}
                                                                onClick={(event) => this.openMultiBoxShipmentView(item, event)}
                                                                title="Open this shipment (Ctrl+click to open in a new tab)"
                                                            >
                                                                📦 {item.baseName} ({item.boxes.length} boxes)
                                                            </a>
                                                        </div>
                                                    </td>
                                                    <td>{item.vendorName}</td>
                                                    <td>{new Date(item.dateCreated).toISOString().split('T')[0]}</td>
                                                    <td>{this.getFirstPurchaseOrderNumber(item.boxes[0]) || '-'}</td>
                                                    <td>{item.trackingNumber || '-'}</td>
                                                    <td>{item.totalItems}</td>
                                                    <td>
                                                        <span className={`multi-box-shipment-status ${item.isFullyReceived ? 'received' : item.hasAnyReceiving ? 'partially-received' : ''}`}>
                                                            {item.isFullyReceived ? 'Received' : item.hasAnyReceiving ? 'Partially Received' : 'Pending'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {item.dateReceived ? 
                                                            new Date(item.dateReceived).toISOString().split('T')[0] : 
                                                            '-'
                                                        }
                                                    </td>
                                                    <td>
                                                        {item.boxes[0]?.fileUrl ? (
                                                            <span className="multi-box-shipment-file"
                                                                  onClick={() => this.download(item.boxes[0].fileUrl, item.boxes[0].fileName)}>
                                                                {item.boxes[0].fileName || item.boxes[0].fileUrl.split('/').pop() || 'Download'}
                                                            </span>
                                                        ) : '-'}
                                                    </td>
                                                </tr>
                                                {isExpanded && (
                                                    <>
                                                        {item.boxes.map((box, index) => (
                                                            <tr key={`${item.id}-box-${box.id}`} className="expanded-box-row" style={{ 
                                                                backgroundColor: '#f8f9fa',
                                                                borderLeft: '3px solid #dee2e6'
                                                            }}>
                                                                <td className="text-left" style={{ paddingLeft: '20px' }}>
                                                                    <span 
                                                                        style={{ 
                                                                            color: '#666', 
                                                                            fontSize: '14px',
                                                                            cursor: 'pointer',
                                                                            textDecoration: 'none'
                                                                        }}
                                                                        onClick={(event) => this.openPackingSlipBoxPage(box, event)}
                                                                        title="Click to edit this box (Ctrl+click to open in a new tab)"
                                                                    >
                                                                        📦 {box.name}
                                                                    </span>
                                                                </td>
                                                                <td>{box.vendorName}</td>
                                                                <td>{new Date(box.dateCreated).toISOString().split('T')[0]}</td>
                                                                <td>{this.getFirstPurchaseOrderNumber(box) || '-'}</td>
                                                                <td>{box.trackingNumber || '-'}</td>
                                                                <td>{box.items?.length || 0}</td>
                                                                <td>
                                                                    <span className={`packing-slip-status ${box.isFullyReceived ? 'received' : box.hasAnyReceiving ? 'partially-received' : ''}`}>
                                                                        {box.isFullyReceived ? 'Received' : box.hasAnyReceiving ? 'Partially Received' : 'Pending'}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    {box.dateReceived ? 
                                                                        new Date(box.dateReceived).toISOString().split('T')[0] : 
                                                                        '-'
                                                                    }
                                                                </td>
                                                                <td>
                                                                    {box.fileUrl ? (
                                                                        <span className="packing-slip-file"
                                                                              onClick={() => this.download(box.fileUrl, box.fileName)}>
                                                                            {box.fileName || 'Download'}
                                                                        </span>
                                                                    ) : '-'}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </>
                                                )}
                                            </React.Fragment>
                                        );
                                    } else {
                                        // Render regular packing slip row
                                        const packingSlip = item;
                                        const packingSlipFullyReceived = isPackingSlipFullyReceived(packingSlip);
                                        return (
                                                <tr key={packingSlip.id} className={`packing-slip-row ${packingSlipFullyReceived ? 'received' : packingSlip.hasAnyReceiving ? 'partially-received' : ''}`}>
                                                    <td className="text-left" style={{ paddingLeft: '37px' }}>
                                                        <span className="packing-slip-name"
                                                            onClick={(event) => this.openPackingSlipBoxPage(packingSlip, event)}
                                                            title="Click to edit this packing slip (Ctrl+click to open in a new tab)">
                                                            {packingSlip.name}
                                                        </span>
                                                    </td>
                                                <td>{packingSlip.vendorName}</td>
                                                <td>{new Date(packingSlip.dateCreated).toISOString().split('T')[0]}</td>
                                                <td>{this.getFirstPurchaseOrderNumber(packingSlip) || '-'}</td>
                                                <td>{packingSlip.trackingNumber || '-'}</td>
                                                <td>{packingSlip.items?.length || 0}</td>
                                                <td>
                                                    <span className={`packing-slip-status ${packingSlipFullyReceived ? 'received' : packingSlip.hasAnyReceiving ? 'partially-received' : ''}`}>
                                                        {packingSlipFullyReceived ? 'Received' : packingSlip.hasAnyReceiving ? 'Partially Received' : 'Pending'}
                                                    </span>
                                                </td>
                                                <td>
                                                    {packingSlip.dateReceived ? 
                                                        new Date(packingSlip.dateReceived).toISOString().split('T')[0] : 
                                                        '-'
                                                    }
                                                </td>
                                                <td>
                                                    {packingSlip.fileUrl ? (
                                                        <span className="packing-slip-file"
                                                            onClick={() => this.download(packingSlip.fileUrl, packingSlip.fileName)}>
                                                            {packingSlip.fileName || 'Download'}
                                                        </span>
                                                    ) : '-'}
                                                </td>
                                            </tr>
                                        );
                                    }
                                })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {this.state.packingSlipDrawerOpen && (
                        <DrawerMenuRight 
                            title={this.state.isEditMode ? "Edit Packing Slip" : "Create Packing Slip"} 
                            customWidth={1300}
                            open={this.state.packingSlipDrawerOpen}
                            onClose={this.handleClosePackingSlipDrawer}
                            hasUnsavedChanges={
                                this.state.packingSlipName !== '' ||
                                this.state.packingSlipTrackingNumber !== '' ||
                                this.state.selectedVendor !== '' ||
                                Object.values(this.state.boxesSelectedParts).some(box => Object.keys(box).length > 0) ||
                                this.state.packingSlipFiles.length > 0
                            }
                            onSubmit={this.handleCreatePackingSlip}
                            submitConfirmMessage={this.state.isEditMode ? "Are you sure you want to update this packing slip?" : "Are you sure you want to create this packing slip?"}
                        >
                            <div>
                                 <div>
                                     <TextField
                                         label="Packing Slip Name"
                                         autoComplete='off'
                                         variant="outlined"
                                         value={this.state.packingSlipName}
                                         onChange={(e) => this.setState({packingSlipName: e.target.value})}
                                         placeholder="Enter packing slip name..."
                                         className="create-packing-slip-name"
                                     />
                                     
                                     <TextField
                                         label="Tracking Number (Optional)"
                                         autoComplete='off'
                                         variant="outlined"
                                         value={this.state.packingSlipTrackingNumber}
                                         onChange={(e) => this.setState({packingSlipTrackingNumber: e.target.value})}
                                         placeholder="Enter tracking number..."
                                         className="create-packing-slip-tracking"
                                     />
                                     <div className="create-packing-slip-form">
                                          <div className="create-packing-slip-vendor">
                                              <Autocomplete
                                                  options={this.state.vendorNames}
                                                  value={this.state.selectedVendor}
                                                  onChange={(event, newValue) => this.handleVendorSelect(newValue)}
                                                  renderInput={(params) => (
                                                      <TextField
                                                          {...params}
                                                          label="Select Vendor"
                                                          variant="outlined"
                                                          placeholder="Type to search vendors..."
                                                      />
                                                  )}
                                                  ListboxProps={{
                                                      style: {
                                                          maxHeight: '400px', // Approximately 30 items at ~20px each for 100 options
                                                          overflow: 'auto'
                                                      }
                                                  }}
                                                  noOptionsText="No vendors found"
                                                  clearOnBlur={false}
                                                  selectOnFocus
                                                  clearOnEscape
                                                  autoHighlight
                                              />
                                          </div>
                                         <FormControl variant="outlined" className="create-packing-slip-boxes">
                                             <InputLabel>Boxes</InputLabel>
                                             <Select
                                                 value={this.state.numberOfBoxes}
                                                 onChange={(e) => this.handleNumberOfBoxesChange(parseInt(e.target.value))}
                                                 label="Boxes"
                                             >
                                                 {Array.from({length: 250}, (_, i) => i + 1).map(num => (
                                                     <MenuItem key={num} value={num}>{num}</MenuItem>
                                                 ))}
                                             </Select>
                                         </FormControl>
                                         <div className="create-packing-slip-file">
                                             {this.state.originalFileName && (
                                                 <Typography variant="body2" color="textSecondary" className="create-packing-slip-original-file">
                                                     💾 Original file "{this.state.originalFileName}" will be cloned if no new file is uploaded
                                                 </Typography>
                                             )}
                                             <FilePond
                                                 files={this.state.packingSlipFiles}
                                                 onupdatefiles={(fileItems) => {
                                                     this.setState({
                                                         packingSlipFiles: fileItems.map(fileItem => fileItem.file)
                                                     });
                                                 }}
                                                 allowMultiple={false}
                                                 maxFiles={1}
                                                 allowFileTypeValidation={false}
                                                 allowSyncAcceptAttribute={false}
                                                 maxFileSize="25MB"
                                                 labelIdle='(Optional) Drag & Drop your packing slip file or <span class="filepond--label-action">Browse</span>'
                                             />
                                         </div>
                                     </div>
                                 </div>
                                {this.state.selectedVendor && (
                                    <>
                                                                                 <center className="create-packing-slip-submit">
                                             {this.state.creatingPackingSlip ?
                                                 <CircularProgress/>
                                                 :
                                                 <Button 
                                                     variant="contained"
                                                     color="primary"
                                                     onClick={this.handleCreatePackingSlip}
                                                     className="success-button-lg"
                                                 >
                                                     {this.state.isEditMode ? 'Update Packing Slip' : 'Create Packing Slip'}
                                                 </Button>
                                             }
                                         </center>
                                        
                                        
                                        {/* Validation Errors Display */}
                                        {(() => {
                                            // Hide validation errors while loading outstanding parts
                                            if (this.state.loadingOutstandingParts) {
                                                return null;
                                            }
                                            
                                            const validationErrors = this.getValidationErrors();
                                            if (validationErrors.length > 0) {
                                                const issueLabel = validationErrors.length === 1 ? '1 quantity issue' : `${validationErrors.length} quantity issues`;
                                                return (
                                                    <div className="validation-errors">
                                                        <details className="validation-errors-details">
                                                            <summary>
                                                                <span className="validation-errors-title">{issueLabel}</span>
                                                                {validationErrors.length === 1 && (
                                                                    <span className="validation-errors-preview">
                                                                        {validationErrors[0].partNumber} rev {validationErrors[0].revision}: {validationErrors[0].message}
                                                                    </span>
                                                                )}
                                                            </summary>
                                                            <div className="validation-errors-list">
                                                                {validationErrors.map(error => (
                                                                    <div key={error.partId} className="validation-error-item">
                                                                        <span className="validation-error-part">
                                                                            {error.partNumber} rev {error.revision}
                                                                        </span>
                                                                        <span className="validation-error-message">
                                                                            {error.message}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </details>
                                                        <FormControlLabel
                                                            className="validation-override"
                                                            control={
                                                                <Checkbox
                                                                    checked={this.state.overrideValidation}
                                                                    onChange={(e) => this.setState({ overrideValidation: e.target.checked })}
                                                                    color="primary"
                                                                    size="small"
                                                                />
                                                            }
                                                            label="Override and save anyway"
                                                        />
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })()}
                                        
                                        {/* Box Tabs */}
                                         {this.state.numberOfBoxes > 1 && (
                                             <div className="box-tabs-container">
                                                 <div className="box-tabs-header">
                                                     <div className="box-tabs-wrapper">
                                                         {Array.from({length: this.state.numberOfBoxes}, (_, i) => i + 1).map(boxNumber => (
                                                             <Button
                                                                 key={boxNumber}
                                                                 variant={this.state.currentBoxTab === boxNumber ? "contained" : "outlined"}
                                                                 color={this.state.currentBoxTab === boxNumber ? "primary" : "default"}
                                                                 onClick={() => this.handleBoxTabChange(boxNumber)}
                                                                 className={`box-tab-button ${this.state.currentBoxTab === boxNumber ? 'active' : 'inactive'}`}
                                                             >
                                                                 Box {boxNumber}
                                                                 {Object.keys(this.state.boxesSelectedParts[boxNumber] || {}).length > 0 && (
                                                                     <span className={`box-tab-count ${this.state.currentBoxTab === boxNumber ? 'active' : 'inactive'}`}>
                                                                         {Object.keys(this.state.boxesSelectedParts[boxNumber] || {}).length}
                                                                     </span>
                                                                 )}
                                                             </Button>
                                                         ))}
                                                     </div>
                                                 </div>
                                                
                                                                                                 {/* Clone Buttons */}
                                                 <div className="clone-actions">
                                                     <Typography variant="body2" className="clone-actions-label">
                                                         Clone Actions:
                                                     </Typography>
                                                     <FormControl variant="outlined" size="small" className="clone-select">
                                                         <InputLabel>Clone To</InputLabel>
                                                         <Select
                                                             label="Clone To"
                                                             onChange={(e) => {
                                                                 this.cloneToBox(parseInt(e.target.value));
                                                                 e.target.value = '';
                                                             }}
                                                             value=""
                                                         >
                                                             {Array.from({length: this.state.numberOfBoxes}, (_, i) => i + 1)
                                                                 .filter(num => num !== this.state.currentBoxTab)
                                                                 .map(num => (
                                                                     <MenuItem key={num} value={num}>Box {num}</MenuItem>
                                                                 ))}
                                                         </Select>
                                                     </FormControl>
                                                     <FormControl variant="outlined" size="small" className="clone-select">
                                                         <InputLabel>Clone From</InputLabel>
                                                         <Select
                                                             label="Clone From"
                                                             onChange={(e) => {
                                                                 this.cloneFromBox(parseInt(e.target.value));
                                                                 e.target.value = '';
                                                             }}
                                                             value=""
                                                         >
                                                             {Array.from({length: this.state.numberOfBoxes}, (_, i) => i + 1)
                                                                 .filter(num => num !== this.state.currentBoxTab)
                                                                 .map(num => (
                                                                     <MenuItem key={num} value={num}>Box {num}</MenuItem>
                                                                 ))}
                                                         </Select>
                                                     </FormControl>
                                                 </div>
                                            </div>
                                        )}
                                        <div className="parts-selection-controls">
                                             <Button onClick={this.handleSelectAll}>Select All</Button>
                                             <Button onClick={this.handleSelectNone}>Select None</Button>
                                             <div className="filter-toggle" onClick={this.handleShowSelectedToggle}>
                                                  <Checkbox checked={this.state.showSelected} onChange={() => {}} size="small"/>
                                                  <span className="filter-label">Show Selected</span>
                                             </div>
                                             <TextField
                                                 label="Search by PN, PO #, or Description"
                                                 autoComplete='off'
                                                 variant="outlined"
                                                 value={this.state.partsSearchTerm}
                                                 onChange={this.handlePartsSearch}
                                                 className="parts-search"
                                                 size="small"
                                             />
                                             <Typography variant="body2" className="parts-selection-status">
                                                 {this.state.showSelected ? 
                                                     `${this.filterOutstandingParts(this.state.outstandingParts).length} selected parts shown` :
                                                     `${Object.keys(this.state.boxesSelectedParts[this.state.currentBoxTab] || {}).length} of ${this.filterOutstandingParts(this.state.outstandingParts).length} parts selected in Box ${this.state.currentBoxTab}`
                                                 }
                                             </Typography>
                                         </div>
                                        
                                        <div className="parts-table-container-create">
                                            <table className="parts-table">
                                                <thead>
                                                    <tr>
                                                        <th style={{minWidth: 50}}>Select</th>
                                                        <th 
                                                            style={{minWidth: 60, cursor: 'pointer', userSelect: 'none'}}
                                                            onClick={() => this.handleSort('purchaseOrderNumber')}
                                                        >
                                                            PO # {this.state.sortBy === 'purchaseOrderNumber' && (this.state.sortDirection === 'asc' ? '↑' : '↓')}
                                                        </th>
                                                        <th 
                                                            style={{minWidth: 160, cursor: 'pointer', userSelect: 'none'}}
                                                            onClick={() => this.handleSort('orderDate')}
                                                        >
                                                            PO Date {this.state.sortBy === 'orderDate' && (this.state.sortDirection === 'asc' ? '↑' : '↓')}
                                                        </th>
                                                        <th 
                                                            style={{minWidth: 110, cursor: 'pointer', userSelect: 'none'}}
                                                            onClick={() => this.handleSort('partNumber')}
                                                        >
                                                            PN {this.state.sortBy === 'partNumber' && (this.state.sortDirection === 'asc' ? '↑' : '↓')}
                                                        </th>
                                                        <th style={{minWidth: 60}}>Ordered</th>
                                                        <th style={{minWidth: 60}}>Received</th>
                                                        <th style={{minWidth: 90}}>On Other Slips</th>
                                                        <th style={{minWidth: 60}}>Available</th>
                                                        <th style={{minWidth: 60}}>Quantity</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {this.filterOutstandingParts(this.state.outstandingParts).map((part) => {
                                                        const isOveraged = this.isPartOveraged(part.id);
                                                                                                                 return (
                                                         <tr key={part.id} className={isOveraged ? 'parts-table-row-overaged' : ''}>
                                                             <td>
                                                                 <Checkbox
                                                                     checked={!!this.state.boxesSelectedParts[this.state.currentBoxTab]?.[part.id]}
                                                                     onChange={(e) => this.handleBoxPartSelect(part.id, e.target.checked)}
                                                                     style={{
                                                                         color: isOveraged ? '#f44336' : undefined
                                                                     }}
                                                                 />
                                                             </td>
                                                             <td><PurchaseLineLink part={part} /></td>
                                                             <td>{formatUtcForDisplay(part.orderDate)}</td>
                                                             <td>
                                                                 <PartDisplay 
                                                                     part={{
                                                                         partId: part.partNumber || part.partFamilyName || '-',
                                                                         revision: this.state.parts.find(x => x.part.id === part.partId)?.part?.revision || '',
                                                                         description: part.partDescription || part.partFamilyName || 'Unknown'
                                                                     }}
                                                                     variant="table"
                                                                     classes={this.props.classes}
                                                                 />
                                                             </td>
                                                             <td>{part.quantityOrdered}</td>
                                                             <td>{part.quantityReceived}</td>
                                                             <td style={{color: this.getAdjustedOnOtherSlipsQuantity(part.id) > 0 ? 'orange' : 'inherit', fontWeight: this.getAdjustedOnOtherSlipsQuantity(part.id) > 0 ? 'bold' : 'normal'}}>
                                                                 {this.getAdjustedOnOtherSlipsQuantity(part.id)}
                                                             </td>
                                                             <td style={{color: this.getAdjustedAvailableQuantity(part.id) > 0 ? 'green' : 'red', fontWeight: 'bold'}}>
                                                                 {this.getAdjustedAvailableQuantity(part.id)}
                                                             </td>
                                                             <td>
                                                                 {this.state.boxesSelectedParts[this.state.currentBoxTab]?.[part.id] ? (
                                                                     <TextField
                                                                         type="number"
                                                                         value={this.state.boxesSelectedPartsQuantities[this.state.currentBoxTab]?.[part.id] || ''}
                                                                         onChange={(e) => this.handleBoxPartQuantityChange(part.id, e.target.value)}
                                                                         onKeyDown={(e) => this.handleQuantityInputKeyDown(e, part.id)}
                                                                         inputProps={{
                                                                             min: 1,
                                                                             'data-part-id-packing': part.id
                                                                         }}
                                                                         size="small"
                                                                         className={`parts-table-quantity-input ${isOveraged ? 'overaged' : ''}`}
                                                                         error={isOveraged}
                                                                     />
                                                                 ) : (
                                                                     '-'
                                                                 )}
                                                             </td>
                                                         </tr>
                                                         );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>

                                                                                 {/* Running List of Full Shipping Contents */}
                                         {this.state.numberOfBoxes > 1 && (() => {
                                             // Hide Full Shipment Contents Summary while loading outstanding parts
                                             if (this.state.loadingOutstandingParts) {
                                                 return null;
                                             }
                                             
                                             const {allSelectedParts, allSelectedQuantities} = this.getAllSelectedParts();
                                             const allPartsArray = Object.keys(allSelectedParts);
                                             
                                             if (allPartsArray.length > 0) {
                                                 return (
                                                     <div className="full-shipment-summary">
                                                         <Typography variant="h6" className="full-shipment-summary-title">
                                                             Full Shipment Contents Summary
                                                         </Typography>
                                                         <div className="full-shipment-summary-table">
                                                             <table>
                                                                 <thead>
                                                                     <tr>
                                                                         <th>PO #</th>
                                                                         <th style={{textAlign: 'center'}}>Selected</th>
                                                                         <th>Part Number</th>
                                                                         <th>In Boxes</th>
                                                                         <th style={{textAlign: 'center'}}>Ordered</th>
                                                                         <th style={{textAlign: 'center'}}>Received</th>
                                                                         <th style={{textAlign: 'center'}}>Available</th>
                                                                     </tr>
                                                                 </thead>
                                                                 <tbody>
                                                                     {allPartsArray.map(partId => {
                                                                         const part = this.state.outstandingParts.find(p => p.id === partId);
                                                                         const inBoxes = Object.keys(allSelectedParts[partId]).join(', ');
                                                                         const isOveraged = this.isPartOveraged(partId);
                                                                         const overages = this.getQuantityOverages();
                                                                         
                                                                         return (
                                                                             <tr key={partId} className={isOveraged ? 'full-shipment-summary-row-overaged' : ''}>
                                                                                 <td>{part?.purchaseOrderNumber || '-'}</td>
                                                                                 <td style={{
                                                                                     textAlign: 'center', 
                                                                                     fontWeight: 'bold',
                                                                                     color: isOveraged ? '#f44336' : 'inherit'
                                                                                 }}>
                                                                                     <span className="full-shipment-summary-selected">{allSelectedQuantities[partId]}</span>
                                                                                 </td>
                                                                                 <td>
                                                                                     <PartDisplay 
                                                                                         part={{
                                                                                             partId: part?.partNumber || part?.partFamilyName || '-',
                                                                                             revision: part?.partId ? this.state.parts.find(x => x.part.id === part.partId)?.part?.revision || '' : '',
                                                                                             description: part?.partDescription || part?.partFamilyName || 'Unknown'
                                                                                         }}
                                                                                         variant="table"
                                                                                         classes={this.props.classes}
                                                                                     />
                                                                                 </td>
                                                                                 <td className="full-shipment-summary-in-boxes">Box {inBoxes}</td>
                                                                                 <td style={{textAlign: 'center', fontWeight: 'bold'}}>{part?.quantityOrdered || 0}</td>
                                                                                 <td style={{textAlign: 'center', fontWeight: 'bold', color: (part?.quantityReceived || 0) > 0 ? 'green' : 'gray'}}>
                                                                                     {part?.quantityReceived || 0}
                                                                                 </td>
                                                                                 <td style={{textAlign: 'center', fontWeight: 'bold', color: (part?.quantityAvailable || 0) > 0 ? 'green' : 'red'}}>
                                                                                     {part?.quantityAvailable || 0}
                                                                                 </td>
                                                                             </tr>
                                                                         );
                                                                     })}
                                                                 </tbody>
                                                             </table>
                                                         </div>
                                                     </div>
                                                 );
                                             }
                                             return null;
                                         })()}
                                    </>
                                )}
                            </div>
                        </DrawerMenuRight>
                    )}

                    <PackingSlipDetails
                        open={this.state.packingSlipDetailsOpen}
                        onClose={this.handleClosePackingSlipDetails}
                        packingSlipId={this.state.selectedPackingSlipId}
                        onUpdate={this.handlePackingSlipDetailsUpdate}
                        parts={this.state.parts}
                        partFamilies={this.state.partFamilies}
                        packingSlips={this.state.packingSlips}
                    />

                    {/* Multi-Box Shipment View */}
                    {this.state.multiBoxShipmentViewOpen && this.state.selectedMultiBoxShipment && (
                        <DrawerMenuRight 
                            title={`Multi-Box Shipment: ${this.state.selectedMultiBoxShipment.baseName}`}
                            customWidth={1200}
                            open={this.state.multiBoxShipmentViewOpen}
                            onClose={this.closeMultiBoxShipmentView}
                        >
                            <div style={{ marginBottom: 20, textAlign: 'right', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => this.openEditMultiBoxShipment(this.state.selectedMultiBoxShipment.boxes, this.state.selectedMultiBoxShipment.baseName, 1)}
                                    className="success-button-lg"
                                >
                                    Edit Shipment
                                </Button>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={this.deleteEntireShipment}
                                    disabled={this.state.deletingEntireShipment}
                                    className="delete-button-lg"
                                >
                                    {this.state.deletingEntireShipment ? 'Deleting...' : 'Delete Shipment'}
                                </Button>
                            </div>
                            <div className="multi-box-shipment-view">
                                <div className="shipment-overview">
                                    <div className="shipment-overview-grid">
                                        <div className="shipment-overview-item">
                                            <Typography variant="body2" className="shipment-overview-label">Vendor</Typography>
                                            <Typography variant="body2" className="shipment-overview-value">
                                                {this.state.selectedMultiBoxShipment.vendorName}
                                            </Typography>
                                        </div>
                                        <div className="shipment-overview-item">
                                            <Typography variant="body2" className="shipment-overview-label">Date Created</Typography>
                                            <Typography variant="body2" className="shipment-overview-value">
                                                {formatUtcForDisplay(this.state.selectedMultiBoxShipment.dateCreated)}
                                            </Typography>
                                        </div>
                                        <div className="shipment-overview-item">
                                            <Typography variant="body2" className="shipment-overview-label">Tracking</Typography>
                                            <Typography variant="body2" className="shipment-overview-value">
                                                {this.state.selectedMultiBoxShipment.trackingNumber || '-'}
                                            </Typography>
                                        </div>
                                        <div className="shipment-overview-item">
                                            <Typography variant="body2" className="shipment-overview-label">Boxes</Typography>
                                            <Typography variant="body2" className="shipment-overview-value">
                                                {this.state.selectedMultiBoxShipment.boxes.length}
                                            </Typography>
                                        </div>
                                        <div className="shipment-overview-item">
                                            <Typography variant="body2" className="shipment-overview-label">Items</Typography>
                                            <Typography variant="body2" className="shipment-overview-value">
                                                {this.state.selectedMultiBoxShipment.totalItems}
                                            </Typography>
                                        </div>
                                        <div className="shipment-overview-item">
                                            <Typography variant="body2" className="shipment-overview-label">Status</Typography>
                                            <Typography variant="body2" className={`shipment-overview-value ${this.state.selectedMultiBoxShipment.isFullyReceived ? 'received' : this.state.selectedMultiBoxShipment.hasAnyReceiving ? 'partially-received' : ''}`}>
                                                {this.state.selectedMultiBoxShipment.isFullyReceived ? 'Fully Received' : 
                                                 this.state.selectedMultiBoxShipment.hasAnyReceiving ? 'Partially Received' : 'Pending'}
                                            </Typography>
                                        </div>
                                        <div className="shipment-overview-item">
                                            <Typography variant="body2" className="shipment-overview-label">File</Typography>
                                            <Typography variant="body2" className="shipment-overview-value">
                                                {this.state.selectedMultiBoxShipment.boxes[0]?.fileUrl ? (
                                                    <span className="shipment-overview-file"
                                                          onClick={() => this.download(this.state.selectedMultiBoxShipment.boxes[0].fileUrl, this.state.selectedMultiBoxShipment.boxes[0].fileName)}>
                                                        {this.state.selectedMultiBoxShipment.boxes[0].fileName || this.state.selectedMultiBoxShipment.boxes[0].fileUrl.split('/').pop() || 'Download File'}
                                                    </span>
                                                ) : '-'}
                                            </Typography>
                                        </div>
                                    </div>
                                </div>

                                <div className="multi-box-view-tabs-container">
                                    <Tabs
                                        value={this.state.multiBoxShipmentViewMode}
                                        onChange={this.handleMultiBoxShipmentViewModeChange}
                                        indicatorColor="primary"
                                        textColor="primary"
                                        variant="fullWidth"
                                    >
                                        <Tab value="per-box" label="Per Box View" />
                                        <Tab value="complete-slip" label="Complete Packing Slip" />
                                    </Tabs>
                                </div>

                                {this.state.multiBoxShipmentViewMode === 'per-box' ? (
                                    <>
                                        {/* Individual Boxes */}
                                        <Typography variant="h6" className="individual-boxes-title">
                                            Box Details
                                        </Typography>
                                        
                                        <div className="individual-boxes-grid">
                                            {this.state.selectedMultiBoxShipment.boxes.map((box, index) => {
                                                const boxFullyReceived = isPackingSlipFullyReceived(box);
                                                return (
                                                <div key={box.id} className={`individual-box ${boxFullyReceived ? 'received' : box.hasAnyReceiving ? 'partially-received' : 'not-received'}`}>
                                                {/* Box Header */}
                                                    <div className="box-header">
                                                        <div className="box-header-left">
                                                            <Typography
                                                                variant="subtitle1"
                                                                className="box-name"
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={(event) => this.openPackingSlipBoxPage(box, event)}
                                                                title="Click to edit this box (Ctrl+click to open in a new tab)"
                                                            >
                                                                {box.name}
                                                            </Typography>
                                                            <Typography variant="body2" className={`box-status ${boxFullyReceived ? 'fully-received' : box.hasAnyReceiving ? 'partially-received' : 'not-received'}`}>
                                                                {boxFullyReceived ? 'FULLY RECEIVED' : box.hasAnyReceiving ? 'PARTIALLY RECEIVED' : 'NOT RECEIVED'}
                                                            </Typography>
                                                            {box.dateReceived ?
                                                                <Typography variant="body2" className='box-items-count'>
                                                                    {`Received: ${formatUtcForDisplay(box.dateReceived)}`}
                                                                </Typography>
                                                            : ''}
                                                            <Typography variant="body2" className="box-items-count">
                                                                {box.items?.length || 0} {box.items?.length === 1 ? 'item' : 'items'}
                                                            </Typography>
                                                        </div>
                                                        <div className="box-header-right">
                                                            {!boxFullyReceived && (
                                                                <>
                                                                    <Button
                                                                        variant="contained"
                                                                        color="primary"
                                                                        size="small"
                                                                        onClick={() => this.fullyReceiveBox(box.id)}
                                                                        disabled={this.state.isReceiving}
                                                                        className="box-button"
                                                                    >
                                                                        RECEIVE ALL
                                                                    </Button>
                                                                    <Button
                                                                        variant="outlined"
                                                                        color="primary"
                                                                        size="small"
                                                                        onClick={() => this.startReceivingBox(box.id)}
                                                                        disabled={this.state.isReceiving || this.state.receivingBoxId === box.id}
                                                                        className="box-button"
                                                                    >
                                                                        CUSTOM RECEIVE
                                                                    </Button>
                                                                </>
                                                            )}
                                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    onClick={(event) => this.openPackingSlipDetails(box, event)}
                                                                    className="box-button view-edit"
                                                                >
                                                                    VIEW
                                                                </Button>
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    onClick={() => this.openEditBoxFromMultiBoxView(box)}
                                                                    className="box-button edit"
                                                                >
                                                                    EDIT
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Items in this box */}
                                                    {box.items && box.items.length > 0 && (
                                                        <div className="box-items-container">
                                                            <table className="box-items-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Part Number</th>
                                                                        <th className="description">Description</th>
                                                                        <th className="expected">Expected</th>
                                                                        <th className="received">Received</th>
                                                                        {this.state.receivingBoxId === box.id && (
                                                                            <th className="receive-qty">Receive Qty</th>
                                                                        )}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {box.items.sort((a, b) => {
                                                                        const partInfoA = this.getPartInfo(a);
                                                                        const partInfoB = this.getPartInfo(b);
                                                                        return partInfoA.partId.localeCompare(partInfoB.partId);
                                                                    }).map(item => {
                                                                        const partInfo = this.getPartInfo(item);
                                                                        const remainingToReceive = item.quantityExpected - item.quantityReceived;
                                                                        const isReceiving = this.state.receivingBoxId === box.id;
                                                                        const receivingQuantity = this.state.receivingQuantities[item.id] || 0;
                                                                        
                                                                        return (
                                                                        <tr key={item.id}>
                                                                            <td>{partInfo.partId}</td>
                                                                            <td className="description-ellipsis text-left">{partInfo.description}</td>
                                                                            <td style={{textAlign: 'center'}}>{item.quantityExpected}</td>
                                                                            <td style={{textAlign: 'center'}}>{item.quantityReceived}</td>
                                                                            {isReceiving && (
                                                                                <td style={{textAlign: 'center'}}>
                                                                                    <TextField
                                                                                        type="number"
                                                                                        value={receivingQuantity || ''}
                                                                                        onChange={(e) => this.updateReceivingQuantity(item.id, e.target.value)}
                                                                                        variant="outlined"
                                                                                        size="small"
                                                                                        style={{textAlign: 'center'}}
                                                                                        className="box-receiving-quantity"
                                                                                        inputProps={{ min: 0, max: remainingToReceive }}
                                                                                    />
                                                                                </td>
                                                                            )}
                                                                        </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                            
                                                            {/* Receiving controls */}
                                                            {this.state.receivingBoxId === box.id && (
                                                                <div className="receiving-controls">
                                                                    <Button
                                                                        variant="outlined"
                                                                        size="small"
                                                                        onClick={this.cancelReceivingBox}
                                                                        disabled={this.state.isReceiving}
                                                                        className="receiving-button"
                                                                    >
                                                                        Cancel
                                                                    </Button>
                                                                    <Button
                                                                        variant="contained"
                                                                        color="primary"
                                                                        size="small"
                                                                        onClick={() => this.submitReceiving(box.id)}
                                                                        disabled={this.state.isReceiving}
                                                                        className="receiving-button"
                                                                    >
                                                                        {this.state.isReceiving ? (
                                                                            <>
                                                                                <CircularProgress size={16} className="receiving-loading" />
                                                                                Receiving...
                                                                            </>
                                                                        ) : 'Submit Receiving'}
                                                                    </Button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                ) : (
                                    <div className="shipment-summary-view">
                                        <Typography variant="h6" className="individual-boxes-title">
                                            Complete Packing Slip Summary
                                        </Typography>
                                        <Typography variant="body2" className="shipment-summary-subtitle">
                                            Combined totals across all boxes in this shipment.
                                        </Typography>
                                        <div className="box-items-container">
                                            <table className="box-items-table shipment-summary-table">
                                                <thead>
                                                    <tr>
                                                        <th>PO #</th>
                                                        <th>Part Number</th>
                                                        <th className="description">Description</th>
                                                        <th className="expected">Expected</th>
                                                        <th className="received">Received</th>
                                                        <th className="expected">Outstanding</th>
                                                        <th className="expected">Boxes</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {this.getShipmentSummaryRows(this.state.selectedMultiBoxShipment).map(row => (
                                                        <tr key={row.key}>
                                                            <td>{row.purchaseOrderNumber}</td>
                                                            <td>{row.partNumber}</td>
                                                            <td className="description-ellipsis text-left">{row.description}</td>
                                                            <td style={{ textAlign: 'center' }}>{row.quantityExpected}</td>
                                                            <td style={{ textAlign: 'center' }}>{row.quantityReceived}</td>
                                                            <td style={{ textAlign: 'center' }}>{row.quantityOutstanding}</td>
                                                            <td style={{ textAlign: 'center' }}>{row.boxCount}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </DrawerMenuRight>
                    )}
                </div>
            </DashboardTemplate>
        );
    }
}

let globalProps;

AdminPackingSlips.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(AdminPackingSlips); 